import React, { useState, Fragment } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Encounter, Patient, Vitals, StatDose, MaternityFile } from '../../types';
import { MaternityErrorBoundary } from '../../components/ui/MaternityErrorBoundary';
import { Activity, ArrowRight, History, Syringe, Trash2, Baby, Save, CheckCircle } from 'lucide-react';
interface Props {}
export const NursingDashboard: React.FC<Props> = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser,
    getPatientEncounters,
    maternityFiles,
    updateMaternityFile
  } = useData();
  const [activeTab, setActiveTab] = useState<'vitals' | 'medications' | 'maternity'>('vitals');
  const [selectedEncounter, setSelectedEncounter] = useState<Encounter | null>(null);
  const [selectedMaternityFileRaw, setSelectedMaternityFileRaw] = useState<MaternityFile | null>(null);
  // Older maternity files (and partially-saved drafts) may be missing the deeply
  // nested objects the form reads (`admission.date`, `presentPregnancy.hpTest`,
  // `delivery.deliverySummary`, etc). Reading into an undefined nested object
  // throws at render time and the entire dashboard goes blank. Normalize every
  // file before handing it to the form so every nested shape is present.
  const normalizeMaternityFile = (file: MaternityFile): MaternityFile => {
    // Treat both undefined and null (Firestore) as "missing".
    const f: any = file || {};
    const obj = (v: any) => v && typeof v === 'object' ? v : {};
    const arr = (v: any) => Array.isArray(v) ? v : [];
    return {
      ...f,
      patient: {
        firstName: '',
        surname: '',
        otherNames: '',
        age: '',
        religion: '',
        churchAttended: '',
        residentialAddress: '',
        locationLandmark: '',
        phone: '',
        ...obj(f.patient),
        nextOfKin: {
          name: '',
          phone: '',
          relationship: '',
          ...obj(obj(f.patient).nextOfKin)
        }
      } as any,
      spouse: {
        firstName: '',
        surname: '',
        otherNames: '',
        occupation: '',
        contact: '',
        ...obj(f.spouse)
      } as any,
      admission: {
        date: '',
        time: '',
        ...obj(f.admission)
      } as any,
      generalExam: {
        temp: '',
        pulse: '',
        respiration: '',
        bp: '',
        oedema: '',
        anemia: '',
        cardiovascularSystem: '',
        respiratorySystem: '',
        centralNervousSystem: '',
        breasts: '',
        varicosities: '',
        vaginalDischarge: '',
        otherAbnormalities: '',
        hb: '',
        abo: '',
        rh: '',
        ...obj(f.generalExam),
        msUrine: {
          sg: '',
          albumen: '',
          acetone: '',
          sugar: '',
          ...obj(obj(f.generalExam).msUrine)
        }
      } as any,
      presentPregnancy: {
        lmp: '',
        quickening: '',
        edd: '',
        gestationAge: '',
        ancSummaryFirstBp: '',
        ancSummaryAtWeeks: '',
        pelvicAssessment: '',
        abnormalities: '',
        ...obj(f.presentPregnancy),
        syphilisTest: {
          date: '',
          result: '',
          treatment: '',
          ...obj(obj(f.presentPregnancy).syphilisTest)
        },
        hpTest: {
          date: '',
          result: '',
          ...obj(obj(f.presentPregnancy).hpTest)
        }
      } as any,
      labourObservations: arr(f.labourObservations),
      delivery: {
        laceration: '',
        anterior: '',
        posterior: '',
        none: '',
        repaired: '',
        membranes: '',
        membranesDate: '',
        membranesTime: '',
        episiotomy: '',
        episiotomyRepaired: '',
        maternalARV: '',
        arvTypes: '',
        uterineMassage: '',
        motherOnHAART: '',
        cytotec: '',
        syntometrine: '',
        firstStageBeganDate: '',
        firstStageBeganTime: '',
        placentaDelivery: '',
        completeDilationDate: '',
        completeDilationTime: '',
        placentaDeliveredDate: '',
        placentaDeliveredTime: '',
        abnormalityAtDilation: '',
        abnormalityAtDilationDesc: '',
        postpartumHemorrhage: '',
        deliveryMethod: '',
        ebl: '',
        abnormalityAtDelivery: '',
        abnormalityAtDeliveryDesc: '',
        complications: '',
        ...obj(f.delivery),
        deliverySummary: arr(obj(f.delivery).deliverySummary)
      } as any,
      newbornEvaluation: {
        ega: '',
        alive: '',
        neonatalDeath: '',
        causeOfDeath: '',
        apgar1: '',
        apgar5: '',
        apgar10: '',
        generalExam: '',
        congenitalMalformation: '',
        congenitalMalformationDesc: '',
        infantARVs: '',
        infantFeeding: '',
        resuscitationMethods: '',
        crownHeelLength: '',
        headCircumference: '',
        skinJaundice: '',
        skinOther: '',
        fontanelles: '',
        eyes: '',
        eyesTreatment: '',
        mouth: '',
        neck: '',
        chest: '',
        abdomen: '',
        umbilicus: '',
        genitalia: '',
        urine: '',
        anus: '',
        meconium: '',
        upperLimbs: '',
        erbParalysis: '',
        lowerLimbs: '',
        ortolaniSign: '',
        back: '',
        reflexesGrasp: '',
        reflexesMoro: '',
        reflexesSucking: '',
        progressNotes: '',
        ...obj(f.newbornEvaluation)
      } as any,
      postDeliveryObservations: arr(f.postDeliveryObservations),
      puerperiumNotes: arr(f.puerperiumNotes)
    };
  };
  const selectedMaternityFile = selectedMaternityFileRaw;
  const setSelectedMaternityFile = (file: MaternityFile | null | ((prev: MaternityFile | null) => MaternityFile | null)) => {
    if (typeof file === 'function') {
      setSelectedMaternityFileRaw(prev => {
        const next = file(prev);
        return next ? normalizeMaternityFile(next) : null;
      });
    } else {
      setSelectedMaternityFileRaw(file ? normalizeMaternityFile(file) : null);
    }
  };
  const [vitals, setVitals] = useState({
    temp: '',
    pulse: '',
    resp: '',
    bp: '',
    sp02: '',
    weight: '',
    height: '',
    bmi: '',
    painScore: '',
    avpu: 'Alert'
  });
  // Medical alerts (Allergies + HIV) — captured at nursing assessment
  const [medicalAlerts, setMedicalAlerts] = useState<{
    allergies: string;
    hivStatus: 'Positive' | 'Negative' | 'Unknown';
  }>({
    allergies: '',
    hivStatus: 'Unknown'
  });
  // Stat Dose form state
  const [statDoseForm, setStatDoseForm] = useState({
    drugName: '',
    dose: '',
    route: 'PO',
    notes: ''
  });
  // Show patients in NURSING stage AND any active patient (so nurses can administer stat doses anytime)
  const pendingEncounters = encounters.filter(e => e.stage === 'NURSING');
  const activeEncounters = encounters.filter(e => e.stage !== 'COMPLETED' && e.stage !== 'NURSING');
  // Past completed encounters for the currently selected patient — drives the
  // "Vitals History" panel. If this is undefined the entire assessment screen
  // crashes at render time, hiding the vitals form.
  const pastEncounters = selectedEncounter ? encounters.filter(e => e.patientId === selectedEncounter.patientId && e.id !== selectedEncounter.id && e.vitals).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()) : [];
  const handleAddStatDose = () => {
    if (!selectedEncounter || !currentUser || !statDoseForm.drugName) return;
    const newDose: StatDose = {
      id: `stat-${Date.now()}`,
      drugName: statDoseForm.drugName,
      dose: statDoseForm.dose,
      route: statDoseForm.route,
      notes: statDoseForm.notes,
      givenAt: new Date().toISOString(),
      givenBy: currentUser.id,
      nurseName: currentUser.fullName
    };
    const existing = selectedEncounter.statDoses || [];
    updateEncounter(selectedEncounter.id, {
      statDoses: [...existing, newDose]
    });
    // Refresh selected encounter to show new dose
    setSelectedEncounter({
      ...selectedEncounter,
      statDoses: [...existing, newDose]
    });
    setStatDoseForm({
      drugName: '',
      dose: '',
      route: 'PO',
      notes: ''
    });
    alert('Stat dose recorded successfully.');
  };
  const handleSaveMaternityDraft = () => {
    if (!selectedMaternityFile) return;
    updateMaternityFile(selectedMaternityFile.id, selectedMaternityFile);
    alert('Maternity file draft saved.');
  };
  const handleCompleteMaternity = () => {
    if (!selectedMaternityFile || !currentUser) return;
    if (confirm('Are you sure you want to complete this maternity visit? It will be moved to the archive.')) {
      updateMaternityFile(selectedMaternityFile.id, {
        ...selectedMaternityFile,
        stage: 'COMPLETED',
        completedAt: new Date().toISOString(),
        completedBy: currentUser.id
      });
      alert('Maternity visit completed and archived.');
      setSelectedMaternityFile(null);
    }
  };
  const activeMaternityFiles = maternityFiles.filter(m => m.stage === 'NURSING');
  const calculateBMI = (weightStr: string, heightStr: string) => {
    const w = parseFloat(weightStr);
    const h = parseFloat(heightStr) / 100; // convert cm to m
    if (w > 0 && h > 0) {
      const bmi = (w / (h * h)).toFixed(1);
      setVitals(prev => ({
        ...prev,
        weight: weightStr,
        height: heightStr,
        bmi
      }));
    } else {
      setVitals(prev => ({
        ...prev,
        weight: weightStr,
        height: heightStr,
        bmi: ''
      }));
    }
  };
  const handleRemoveStatDose = (doseId: string) => {
    if (!selectedEncounter) return;
    const remaining = (selectedEncounter.statDoses || []).filter(d => d.id !== doseId);
    updateEncounter(selectedEncounter.id, {
      statDoses: remaining
    });
    setSelectedEncounter({
      ...selectedEncounter,
      statDoses: remaining
    });
  };
  const handleSaveVitals = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEncounter || !currentUser) return;
    const newVitals: Vitals = {
      ...vitals,
      recordedAt: new Date().toISOString(),
      recordedBy: currentUser.id
    };
    updateEncounter(selectedEncounter.id, {
      vitals: newVitals,
      stage: 'CLINICIAN'
    });
    // Update patient's medical alerts
    const patientToUpdate = patients.find(p => p.id === selectedEncounter.patientId);
    if (patientToUpdate) {
      // In a real app we'd have a function like updatePatient(patientId, data)
      // Assuming it exists or we just update the encounter for now.
    }
    alert('Vitals saved and patient forwarded to Clinician.');
    setSelectedEncounter(null);
    setVitals({
      temp: '',
      pulse: '',
      resp: '',
      bp: '',
      sp02: '',
      weight: '',
      height: '',
      bmi: '',
      painScore: '',
      avpu: 'Alert'
    });
  };
  const patient = selectedEncounter ? patients.find(p => p.id === selectedEncounter.patientId) : null;
  return <div className="space-y-6" data-id="element-3994" data-mp="ih5kboky9ijc">
      <div className="flex justify-between items-center" data-id="element-3995" data-mp="ig465b8n0fxe">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-3996" data-mp="n9yly3l4gar7">Nursing Dashboard</h1>
        <div className="flex space-x-2" data-id="element-3997" data-mp="s8jo48jxp4ia">
          <Badge variant="success" className="text-sm px-3 py-1" data-id="element-3998" data-mp="s7ie87enmo25">
            {pendingEncounters.length} Vitals Pending
          </Badge>
          <Badge variant="info" className="text-sm px-3 py-1" data-id="element-3999" data-mp="1267kf2x6uk2k">
            {activeEncounters.length} Active Patients
          </Badge>
          <Badge variant="pink" className="text-sm px-3 py-1 bg-pink-100 text-pink-800" data-id="element-4000" data-mp="cj3rn75fhemc">
            {activeMaternityFiles.length} Maternity
          </Badge>
        </div>
      </div>

      <div className="border-b border-gray-200" data-id="element-4001" data-mp="m9evktp43nt8">
        <nav className="-mb-px flex space-x-8" data-id="element-4002" data-mp="hhf2nl7glv65">
          <button onClick={() => {
          setActiveTab('vitals');
          setSelectedEncounter(null);
          setSelectedMaternityFile(null);
        }} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'vitals' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-4003" data-mp="hfkg62gjhuzy">
            Vitals Queue
          </button>
          <button onClick={() => {
          setActiveTab('medications');
          setSelectedEncounter(null);
          setSelectedMaternityFile(null);
        }} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'medications' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-4004" data-mp="36xb11jntiba8">
            Medications & Treatments
          </button>
          <button onClick={() => {
          setActiveTab('maternity');
          setSelectedEncounter(null);
          setSelectedMaternityFile(null);
        }} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'maternity' ? 'border-pink-500 text-pink-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-4005" data-mp="1cppcofik40px">
            <Baby className="w-4 h-4 inline-block mr-2 mb-1" data-id="element-4006" data-mp="tsfut2svqvas" />
            Maternity Ward
          </button>
        </nav>
      </div>

      {activeTab === 'vitals' && <div className="space-y-6" data-id="element-4007" data-mp="2o5xj4s13zw6v">
          {!selectedEncounter ? <div className="space-y-4" data-id="element-4008" data-mp="2o31j8y5paxfs">
              <Card title="Awaiting Vitals" noPadding data-id="element-4009" data-mp="o3ehlcdhdeg4">
                <ul className="divide-y divide-gray-200" data-id="element-4010" data-mp="o0sb7g2bt4zh">
                  {pendingEncounters.length > 0 ? pendingEncounters.map(enc => {
              const p = patients.find(pat => pat.id === enc.patientId);
              return <li key={enc.id} className="p-4 hover:bg-gray-50 cursor-pointer flex justify-between items-center" onClick={() => setSelectedEncounter(enc)} data-id="element-4011" data-mp="a33r4ag321vh">
                          <div className="flex items-center" data-id="element-4012" data-mp="3rkwmpc1vc7cu">
                            <div className="bg-blue-100 p-2 rounded-full mr-4" data-id="element-4013" data-mp="3nijiis2rshi6">
                              <Activity className="h-6 w-6 text-blue-600" data-id="element-4014" data-mp="3ngghld02x3yo" />
                            </div>
                            <div data-id="element-4015" data-mp="3dk3q8ls0b273">
                              <p className="text-sm font-medium text-blue-600" data-id="element-4016" data-mp="3dju1t2mm133i">
                                {p?.bmcFileNumber || '—'}
                              </p>
                              <p className="text-lg font-bold text-gray-900" data-id="element-4017" data-mp="1y0w1ljov7zz4">
                                {p?.firstName || 'Patient'} {p?.lastName || ''}
                              </p>
                              <p className="text-sm text-gray-500" data-id="element-4018" data-mp="271u6s5b1idja">
                                {p?.age ?? '—'} yrs • {p?.sex || '—'}
                              </p>
                            </div>
                          </div>
                          <ArrowRight className="h-5 w-5 text-gray-400" data-id="element-4019" data-mp="1fbpt0phscor1" />
                        </li>;
            }) : <li className="p-8 text-center text-gray-500" data-id="element-4020" data-mp="1o2q2f9ug17xy">
                      No patients waiting for vitals
                    </li>}
                </ul>
              </Card>
              <Card title="Active Patients — Record Additional Vitals" noPadding data-id="element-4021" data-mp="vmfe3dpv67tz">
                <ul className="divide-y divide-gray-200" data-id="element-4022" data-mp="1ywjhu9ow9g6j">
                  {activeEncounters.length > 0 ? activeEncounters.map(enc => {
              const p = patients.find(pat => pat.id === enc.patientId);
              return <li key={enc.id} className="p-4 hover:bg-gray-50 cursor-pointer flex justify-between items-center" onClick={() => setSelectedEncounter(enc)} data-id="element-4023" data-mp="23nrs17y1t5kr">
                          <div className="flex items-center" data-id="element-4024" data-mp="1ozf2aspk0iyp">
                            <div className="bg-green-100 p-2 rounded-full mr-4" data-id="element-4025" data-mp="1p0yhsh3dypzf">
                              <Activity className="h-6 w-6 text-green-600" data-id="element-4026" data-mp="1kwqpyb1mstwz" />
                            </div>
                            <div data-id="element-4027" data-mp="30d96abb3jzy2">
                              <p className="text-sm font-medium text-blue-600" data-id="element-4028" data-mp="30geulobwixsq">
                                {p?.bmcFileNumber || '—'}
                              </p>
                              <p className="text-lg font-bold text-gray-900" data-id="element-4029" data-mp="33uwuzx0tug2d">
                                {p?.firstName || 'Patient'} {p?.lastName || ''}
                              </p>
                              <p className="text-sm text-gray-500" data-id="element-4030" data-mp="2tzv3iuj2x1wm">
                                {p?.age ?? '—'} yrs • {p?.sex || '—'} •{' '}
                                <span className="text-gray-400" data-id="element-4031" data-mp="3mmzb979k3uc3">
                                  Stage: {enc.stage}
                                </span>
                              </p>
                            </div>
                          </div>
                          <ArrowRight className="h-5 w-5 text-gray-400" data-id="element-4032" data-mp="3cwmulq4v4lb8" />
                        </li>;
            }) : <li className="p-8 text-center text-gray-500" data-id="element-4033" data-mp="9g7d9pii3eq7">
                      No active patients
                    </li>}
                </ul>
              </Card>
            </div> : <>
              <div className="flex justify-between items-center" data-id="element-4034" data-mp="2ablrwdfccgyt">
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-4035" data-mp="25ij5qqcw89sn">
                  Nursing Assessment
                </h1>
                <Button variant="outline" onClick={() => setSelectedEncounter(null)} data-id="element-4036" data-mp="21iijijjjyub1">
                  Back to Queue
                </Button>
              </div>

              <Card className="bg-blue-50 border-blue-200" data-id="element-4037" data-mp="2t72ch1gaoeim">
                <div className="flex justify-between items-start" data-id="element-4038" data-mp="2p8mksk99qhyv">
                  <div data-id="element-4039" data-mp="2k88aevk72vxo">
                    <h2 className="text-xl font-bold text-gray-900" data-id="element-4040" data-mp="2k7fc8izaxer5">
                      {patient?.firstName || 'Patient'}{' '}
                      {patient?.lastName || ''}
                    </h2>
                    <p className="text-sm text-gray-600" data-id="element-4041" data-mp="18ucsjf1wo92r">
                      BMC: {patient?.bmcFileNumber || '—'} •{' '}
                      {patient?.age ?? '—'} yrs • {patient?.sex || '—'}
                    </p>
                  </div>
                  <div className="text-right" data-id="element-4042" data-mp="togud1dmuzk9">
                    <p className="text-sm font-medium text-red-600" data-id="element-4043" data-mp="yola2p6pvq9f">
                      Allergies: {medicalAlerts.allergies || '—'}
                    </p>
                    <p className="text-sm text-gray-600" data-id="element-4044" data-mp="13mmxtnf38dr8">
                      HIV Status: {medicalAlerts.hivStatus}
                    </p>
                  </div>
                </div>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-4045" data-mp="1hfvzt30phwzq">
                <div className="lg:col-span-2" data-id="element-4046" data-mp="1he1hzxkqekzv">
                  <Card title="Enter Vitals" data-id="element-4047" data-mp="1meflr3fav8s3">
                    <form onSubmit={handleSaveVitals} className="space-y-6" data-id="element-4048" data-mp="1mfqp524u2eio">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-4049" data-mp="1rcr0nskgc9h6">
                        <Input label="Blood Pressure (mmHg)" placeholder="120/80" required value={vitals.bp} onChange={e => setVitals({
                    ...vitals,
                    bp: e.target.value
                  })} data-id="element-4050" data-mp="3o73xk160f4jh" />
                        <Input label="Temperature (°C)" type="number" step="0.1" required value={vitals.temp} onChange={e => setVitals({
                    ...vitals,
                    temp: e.target.value
                  })} data-id="element-4051" data-mp="2evhfp6bblfqm" />
                        <Input label="Pulse (bpm)" type="number" required value={vitals.pulse} onChange={e => setVitals({
                    ...vitals,
                    pulse: e.target.value
                  })} data-id="element-4052" data-mp="yhhfdu8kbo2u" />
                        <Input label="Resp. Rate (cpm)" type="number" required value={vitals.resp} onChange={e => setVitals({
                    ...vitals,
                    resp: e.target.value
                  })} data-id="element-4053" data-mp="m79owz78un9h" />
                        <Input label="SpO2 (%)" type="number" required value={vitals.sp02} onChange={e => setVitals({
                    ...vitals,
                    sp02: e.target.value
                  })} data-id="element-4054" data-mp="10wo1eg649kt3" />
                        <Input label="Pain Score (0-10)" type="number" min="0" max="10" required value={vitals.painScore} onChange={e => setVitals({
                    ...vitals,
                    painScore: e.target.value
                  })} data-id="element-4055" data-mp="3s011zdlgtbjf" />

                        <Input label="Weight (kg)" type="number" step="0.1" required value={vitals.weight} onChange={e => calculateBMI(e.target.value, vitals.height || '')} data-id="element-4056" data-mp="2zerfkga68d8k" />
                        <Input label="Height (cm)" type="number" step="0.1" required value={vitals.height} onChange={e => calculateBMI(vitals.weight || '', e.target.value)} data-id="element-4057" data-mp="21tgeyzkek8y9" />
                        <Input label="BMI" readOnly value={vitals.bmi} className="bg-gray-50" data-id="element-4058" data-mp="2kqhliotluh4b" />
                      </div>

                      <div className="border-t border-gray-200 pt-4" data-id="element-4059" data-mp="38g27lgl21uht">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4060" data-mp="38e8y7tj24w64">
                          AVPU Scale
                        </label>
                        <div className="flex space-x-4" data-id="element-4061" data-mp="3nitvqg96le1q">
                          {['Alert', 'Verbal', 'Pain', 'Unresponsive'].map(level => <label key={level} className="flex items-center" data-id="element-4062" data-mp="69cpj9vqaqu">
                                <input type="radio" name="avpu" value={level} checked={vitals.avpu === level} onChange={e => setVitals({
                        ...vitals,
                        avpu: e.target.value
                      })} className="mr-2" data-id="element-4063" data-mp="55ckvcl5ox31" />
                                {level}
                              </label>)}
                        </div>
                      </div>

                      {/* Medical Alerts (moved from Reception) */}
                      <div className="border-t border-gray-200 pt-4" data-id="element-4064" data-mp="2dfbxwpz4fjns">
                        <h4 className="text-md font-semibold text-gray-900 mb-3" data-id="element-4065" data-mp="28l7p016ap1k2">
                          Medical Alerts
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4066" data-mp="1zlkpjkx4m5tn">
                          <Input label="Known Allergies" placeholder="e.g. Penicillin, Peanuts (or 'None')" required value={medicalAlerts.allergies} onChange={e => setMedicalAlerts({
                      ...medicalAlerts,
                      allergies: e.target.value
                    })} data-id="element-4067" data-mp="1zjhq6aa330c9" />
                          <div data-id="element-4068" data-mp="1dcdvhlpwfzk5">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4069" data-mp="1dbc9im7chsnn">
                              HIV Status
                            </label>
                            <select required value={medicalAlerts.hivStatus} onChange={e => setMedicalAlerts({
                        ...medicalAlerts,
                        hivStatus: e.target.value as 'Positive' | 'Negative' | 'Unknown'
                      })} className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-4070" data-mp="13gkb5g5xv1rt">
                              <option value="Unknown" data-id="element-4071" data-mp="85cq31u2n5p3">Unknown</option>
                              <option value="Positive" data-id="element-4072" data-mp="372uiq4fq32y">Positive</option>
                              <option value="Negative" data-id="element-4073" data-mp="36t3cz7nlpvo">Negative</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end pt-4" data-id="element-4074" data-mp="d60dzen076f3">
                        <Button type="submit" size="lg" data-id="element-4075" data-mp="3lk932cqbhf2i">
                          Save & Forward to Clinician
                        </Button>
                      </div>
                    </form>
                  </Card>

                  {/* Stat / Start Dose Section */}
                  <Card title="Stat / Start Dose Administered" className="mt-6" action={<Syringe className="w-5 h-5 text-pink-500" data-id="element-4077" data-mp="32tiwoe1jfa4w" />} data-id="element-4076" data-mp="3qkewnyg5dbn8">
                    <p className="text-xs text-gray-500 mb-4" data-id="element-4078" data-mp="2xvzgv9p6ypau">
                      Record any medication given to the patient as a start/stat
                      dose. These will appear on the patient file and be visible
                      to the clinician.
                    </p>

                    {/* Existing Stat Doses */}
                    {selectedEncounter.statDoses && selectedEncounter.statDoses.length > 0 && <div className="mb-4 space-y-2" data-id="element-4079" data-mp="2q9tfq2g84gse">
                          {selectedEncounter.statDoses.map(d => <div key={d.id} className="flex justify-between items-start bg-pink-50 border border-pink-200 rounded p-3 text-sm" data-id="element-4080" data-mp="309tx3iuj8mty">
                              <div data-id="element-4081" data-mp="39ylo9orrr33t">
                                <p className="font-bold text-gray-900" data-id="element-4082" data-mp="3a1pzh63niz3z">
                                  {d.drugName}{' '}
                                  <span className="text-gray-600 font-normal" data-id="element-4083" data-mp="3j0bo9izpvad7">
                                    {d.dose} • {d.route}
                                  </span>
                                </p>
                                <p className="text-xs text-gray-600 mt-0.5" data-id="element-4084" data-mp="12dvlvvbojon1">
                                  Given by {d.nurseName} •{' '}
                                  {new Date(d.givenAt).toLocaleString()}
                                </p>
                                {d.notes && <p className="text-xs text-gray-700 mt-1 italic" data-id="element-4085" data-mp="1q496x1vukn84">
                                    {d.notes}
                                  </p>}
                              </div>
                              <button onClick={() => handleRemoveStatDose(d.id)} className="text-gray-400 hover:text-red-600 p-1" title="Remove" data-id="element-4086" data-mp="1h9us10f8bepj">
                                <Trash2 className="w-4 h-4" data-id="element-4087" data-mp="24zyuv5klfafh" />
                              </button>
                            </div>)}
                        </div>}

                    {/* Add New Stat Dose */}
                    <div className="grid grid-cols-2 gap-3 border-t border-gray-200 pt-4" data-id="element-4088" data-mp="26syepw8wan1d">
                      <Input label="Drug Name" className="col-span-2" placeholder="e.g. Paracetamol" value={statDoseForm.drugName} onChange={e => setStatDoseForm({
                  ...statDoseForm,
                  drugName: e.target.value
                })} data-id="element-4089" data-mp="26qlo6up2n4cp" />
                      <Input label="Dose" placeholder="e.g. 500mg" value={statDoseForm.dose} onChange={e => setStatDoseForm({
                  ...statDoseForm,
                  dose: e.target.value
                })} data-id="element-4090" data-mp="2kmfwpclxnrpa" />
                      <div data-id="element-4091" data-mp="3464zjhlngtj3">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4092" data-mp="346y369pbb2x0">
                          Route
                        </label>
                        <select value={statDoseForm.route} onChange={e => setStatDoseForm({
                    ...statDoseForm,
                    route: e.target.value
                  })} className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-4093" data-mp="3972ihwehhrln">
                          <option value="PO" data-id="element-4094" data-mp="2bjtthrf9y0ew">Oral (PO)</option>
                          <option value="IV" data-id="element-4095" data-mp="2gg2dbi4f2hse">Intravenous (IV)</option>
                          <option value="IM" data-id="element-4096" data-mp="1xc77dqeoms4m">Intramuscular (IM)</option>
                          <option value="SC" data-id="element-4097" data-mp="1sl8rut1ipy2f">Subcutaneous (SC)</option>
                          <option value="PR" data-id="element-4098" data-mp="1sjxjsh11i45i">Per Rectum (PR)</option>
                          <option value="Topical" data-id="element-4099" data-mp="1nn74vx0z1elo">Topical</option>
                        </select>
                      </div>
                      <Input label="Notes (optional)" className="col-span-2" placeholder="Reason or observation..." value={statDoseForm.notes} onChange={e => setStatDoseForm({
                  ...statDoseForm,
                  notes: e.target.value
                })} data-id="element-4100" data-mp="1iovwlqqbrc5k" />
                    </div>
                    <div className="mt-4 flex justify-end" data-id="element-4101" data-mp="pydlc2fg64h2">
                      <Button type="button" onClick={handleAddStatDose} disabled={!statDoseForm.drugName} className="bg-pink-600 hover:bg-pink-700 text-white disabled:bg-gray-300" data-id="element-4102" data-mp="pwu52btsjtfa">
                        <Syringe className="w-4 h-4 mr-2" data-id="element-4103" data-mp="3bic2vy83h1no" /> Mark Stat Dose
                        Given
                      </Button>
                    </div>
                  </Card>
                </div>

                <div className="lg:col-span-1" data-id="element-4104" data-mp="3l9gfiraasafc">
                  <Card title="Vitals History" className="h-full" data-id="element-4105" data-mp="3pas8n6sdrdsa">
                    {pastEncounters.length > 0 ? <div className="space-y-4 overflow-y-auto max-h-[500px] pr-2" data-id="element-4106" data-mp="3ubfxpmx1tkjv">
                        {pastEncounters.map(enc => <div key={enc.id} className="bg-gray-50 p-3 rounded border border-gray-200 text-sm" data-id="element-4107" data-mp="320oyjfd75x4">
                            <p className="font-medium text-gray-900 mb-2 border-b pb-1" data-id="element-4108" data-mp="24xkko1l37pty">
                              {new Date(enc.startedAt).toLocaleDateString()}
                            </p>
                            <div className="grid grid-cols-2 gap-2" data-id="element-4109" data-mp="3qflbvaayumhe">
                              <div data-id="element-4110" data-mp="86kt3jh6yb4h">
                                <span className="text-gray-500" data-id="element-4111" data-mp="85jghakj1gkn">BP:</span>{' '}
                                {enc.vitals?.bp}
                              </div>
                              <div data-id="element-4112" data-mp="3cmvq96zcnjkt">
                                <span className="text-gray-500" data-id="element-4113" data-mp="3cjpy1ivfc1kv">Temp:</span>{' '}
                                {enc.vitals?.temp}°C
                              </div>
                              <div data-id="element-4114" data-mp="3lfyy8zr87482">
                                <span className="text-gray-500" data-id="element-4115" data-mp="3gnpclvx58qbl">Pulse:</span>{' '}
                                {enc.vitals?.pulse}
                              </div>
                              <div data-id="element-4116" data-mp="2swa5zqyc3muj">
                                <span className="text-gray-500" data-id="element-4117" data-mp="2owhx1d513eon">SpO2:</span>{' '}
                                {enc.vitals?.sp02}%
                              </div>
                              <div data-id="element-4118" data-mp="2er9rfpkmuu2h">
                                <span className="text-gray-500" data-id="element-4119" data-mp="2jj1dbmsugbma">Weight:</span>{' '}
                                {enc.vitals?.weight}kg
                              </div>
                              <div data-id="element-4120" data-mp="24skaq04h3fnt">
                                <span className="text-gray-500" data-id="element-4121" data-mp="29syhleuc8jk9">BMI:</span>{' '}
                                {enc.vitals?.bmi}
                              </div>
                            </div>
                          </div>)}
                      </div> : <div className="text-center py-8 text-gray-500 flex flex-col items-center" data-id="element-4122" data-mp="2nlo6br1942l2">
                        <History className="w-8 h-8 mb-2 text-gray-400" data-id="element-4123" data-mp="2sj6irreh8zxt" />
                        <p data-id="element-4124" data-mp="1c5zkx43nbbmd">No previous vitals recorded</p>
                      </div>}
                  </Card>
                </div>
              </div>
            </>}
        </div>}

      {activeTab === 'maternity' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-4125" data-mp="3jqy7zbm3mzow">
          <div className="lg:col-span-1" data-id="element-4126" data-mp="3es4xls23zosy">
            <Card title="Active Maternity Files" noPadding data-id="element-4127" data-mp="3et6h8kx6xjma">
              <ul className="divide-y divide-gray-200" data-id="element-4128" data-mp="3a0muoygize5c">
                {activeMaternityFiles.length > 0 ? activeMaternityFiles.map(file => <li key={file.id} className={`p-4 hover:bg-gray-50 cursor-pointer ${selectedMaternityFile?.id === file.id ? 'bg-pink-50' : ''}`} onClick={() => setSelectedMaternityFile(file)} data-id="element-4129" data-mp="3038ri0jm4luz">
                      <div className="flex items-center" data-id="element-4130" data-mp="2r4nai9tshewb">
                        <div className="bg-pink-100 p-2 rounded-full mr-3" data-id="element-4131" data-mp="2m50why64zumg">
                          <Baby className="h-5 w-5 text-pink-600" data-id="element-4132" data-mp="pbpg49kd6e99" />
                        </div>
                        <div data-id="element-4133" data-mp="kc2zrr98ohw8">
                          <p className="text-sm font-medium text-blue-600" data-id="element-4134" data-mp="kcvxy3u4tz2r">
                            {file.bmcFileNumber}
                          </p>
                          <p className="text-base font-bold text-gray-900" data-id="element-4135" data-mp="wugicb4o6ebm">
                            {file.patient.firstName} {file.patient.surname}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-4136" data-mp="16krxkttt09xt">
                            Registered:{' '}
                            {new Date(file.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </li>) : <li className="p-8 text-center text-gray-500" data-id="element-4137" data-mp="iu5r36zg6xpx">
                    <Baby className="mx-auto h-8 w-8 text-gray-300 mb-2" data-id="element-4138" data-mp="nua60skpjke1" />
                    <p data-id="element-4139" data-mp="ns7ajt0scz1x">No active maternity patients</p>
                  </li>}
              </ul>
            </Card>
          </div>

          <div className="lg:col-span-2" data-id="element-4140" data-mp="1z8nthwbjtxtj">
            {selectedMaternityFile ? <MaternityErrorBoundary key={selectedMaternityFile.id} onReset={() => setSelectedMaternityFile(null)} data-id="element-4141" data-mp="2kd80zp0dwdjc">
                <div className="space-y-6" data-id="element-4142" data-mp="2fcbowo5vpl2u">
                  <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm border border-gray-200 sticky top-0 z-10" data-id="element-4143" data-mp="2u3svp1cxsxwz">
                    <div data-id="element-4144" data-mp="2pb9mshp72qyi">
                      <h2 className="text-xl font-bold text-gray-900" data-id="element-4145" data-mp="333z8sm4q3vvm">
                        {selectedMaternityFile.patient?.firstName}{' '}
                        {selectedMaternityFile.patient?.surname}
                      </h2>
                      <p className="text-sm text-gray-500" data-id="element-4146" data-mp="3d2exyjk6wv2h">
                        File: {selectedMaternityFile.bmcFileNumber} | Age:{' '}
                        {selectedMaternityFile.patient?.age}
                      </p>
                    </div>
                    <div className="flex space-x-2" data-id="element-4147" data-mp="1ipyx5ya5y950">
                      <Button variant="outline" onClick={handleSaveMaternityDraft} data-id="element-4148" data-mp="1dq2srxxk0fox">
                        <Save className="w-4 h-4 mr-2" data-id="element-4149" data-mp="3ubhk3h4krexd" /> Save Draft
                      </Button>
                      <Button variant="primary" className="bg-pink-600 hover:bg-pink-700 text-white" onClick={handleCompleteMaternity} data-id="element-4150" data-mp="3fml51043p1n9">
                        <CheckCircle className="w-4 h-4 mr-2" data-id="element-4151" data-mp="36ln7n0o63sdn" /> Complete Visit
                      </Button>
                    </div>
                  </div>

                  <Card title="Admission & General Exam" data-id="element-4152" data-mp="3bin5wperzqu7">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-4153" data-mp="2wp0ocgbhp9dq">
                      <Input type="date" label="Date of Admission" value={selectedMaternityFile.admission.date} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  admission: {
                    ...selectedMaternityFile.admission,
                    date: e.target.value
                  }
                })} data-id="element-4154" data-mp="31m0ne79x9bvc" />
                      <Input type="time" label="Time of Admission" value={selectedMaternityFile.admission.time} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  admission: {
                    ...selectedMaternityFile.admission,
                    time: e.target.value
                  }
                })} data-id="element-4155" data-mp="2r9l18ivxynez" />
                    </div>
                    <h4 className="font-medium text-gray-900 mb-3 border-b pb-1" data-id="element-4156" data-mp="1ylgvjxddeaca">
                      General Examination
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-4157" data-mp="1ayv1xedym63g">
                      <Input label="Temp" value={selectedMaternityFile.generalExam.temp} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    temp: e.target.value
                  }
                })} data-id="element-4158" data-mp="1b0y2utgnhjmy" />
                      <Input label="Pulse" value={selectedMaternityFile.generalExam.pulse} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    pulse: e.target.value
                  }
                })} data-id="element-4159" data-mp="22r2gm99wn8f7" />
                      <Input label="Respiration" value={selectedMaternityFile.generalExam.respiration} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    respiration: e.target.value
                  }
                })} data-id="element-4160" data-mp="2yfrsbkzczuqq" />
                      <Input label="BP" value={selectedMaternityFile.generalExam.bp} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    bp: e.target.value
                  }
                })} data-id="element-4161" data-mp="1i44l9i7t7zvb" />
                      <Input label="Oedema" value={selectedMaternityFile.generalExam.oedema} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    oedema: e.target.value
                  }
                })} data-id="element-4162" data-mp="1yf6c562xh75y" />
                      <Input label="Anemia" value={selectedMaternityFile.generalExam.anemia} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    anemia: e.target.value
                  }
                })} data-id="element-4163" data-mp="1wy94lvjtc7kr" />
                      <Input label="Hb%" value={selectedMaternityFile.generalExam.hb} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    hb: e.target.value
                  }
                })} data-id="element-4164" data-mp="2kmsbq7r4meb9" />
                      <Input label="ABO" value={selectedMaternityFile.generalExam.abo} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    abo: e.target.value
                  }
                })} data-id="element-4165" data-mp="38joorlatsz82" />
                      <Input label="Rh" value={selectedMaternityFile.generalExam.rh} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    rh: e.target.value
                  }
                })} data-id="element-4166" data-mp="33lwu4jisqz06" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-4167" data-mp="jrle157uxar5">
                      <Input label="Cardiovascular System" value={selectedMaternityFile.generalExam.cardiovascularSystem} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    cardiovascularSystem: e.target.value
                  }
                })} data-id="element-4168" data-mp="oouzyaw1f4a8" />
                      <Input label="Respiratory System" value={selectedMaternityFile.generalExam.respiratorySystem} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    respiratorySystem: e.target.value
                  }
                })} data-id="element-4169" data-mp="31wmjpofb3hj8" />
                      <Input label="Central Nervous System" value={selectedMaternityFile.generalExam.centralNervousSystem} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    centralNervousSystem: e.target.value
                  }
                })} data-id="element-4170" data-mp="298qpcqd7m1pm" />
                      <Input label="Breasts" value={selectedMaternityFile.generalExam.breasts} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    breasts: e.target.value
                  }
                })} data-id="element-4171" data-mp="32qkl584u31sf" />
                      <Input label="Varicosities" value={selectedMaternityFile.generalExam.varicosities} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    varicosities: e.target.value
                  }
                })} data-id="element-4172" data-mp="czxrmsibx42z" />
                      <Input label="Vaginal Discharge" value={selectedMaternityFile.generalExam.vaginalDischarge} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    vaginalDischarge: e.target.value
                  }
                })} data-id="element-4173" data-mp="2l7khneboiq3t" />
                    </div>
                    <div className="mt-4" data-id="element-4174" data-mp="s3zik30sluk">
                      <Input label="Other Abnormalities" value={selectedMaternityFile.generalExam.otherAbnormalities} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    otherAbnormalities: e.target.value
                  }
                })} data-id="element-4175" data-mp="tf1qfgthljk" />
                    </div>
                    <h4 className="font-medium text-gray-900 mt-6 mb-3 border-b pb-1" data-id="element-4176" data-mp="17eq29txdznru">
                      MS Urine
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-4177" data-mp="1a0779to2a7vt">
                      <Input label="SG" value={selectedMaternityFile.generalExam.msUrine.sg} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    msUrine: {
                      ...selectedMaternityFile.generalExam.msUrine,
                      sg: e.target.value
                    }
                  }
                })} data-id="element-4178" data-mp="19y36hklglozr" />
                      <Input label="Albumen" value={selectedMaternityFile.generalExam.msUrine.albumen} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    msUrine: {
                      ...selectedMaternityFile.generalExam.msUrine,
                      albumen: e.target.value
                    }
                  }
                })} data-id="element-4179" data-mp="2ceeitfyurte9" />
                      <Input label="Acetone" value={selectedMaternityFile.generalExam.msUrine.acetone} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    msUrine: {
                      ...selectedMaternityFile.generalExam.msUrine,
                      acetone: e.target.value
                    }
                  }
                })} data-id="element-4180" data-mp="3poymttp18wrv" />
                      <Input label="Sugar" value={selectedMaternityFile.generalExam.msUrine.sugar} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    msUrine: {
                      ...selectedMaternityFile.generalExam.msUrine,
                      sugar: e.target.value
                    }
                  }
                })} data-id="element-4181" data-mp="1hjoa37qhwwxr" />
                    </div>
                  </Card>

                  <Card title="Present Pregnancy" data-id="element-4182" data-mp="zcmik4oesdtd">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4" data-id="element-4183" data-mp="zbuq32q3yw7b">
                      <Input type="date" label="LMP" value={selectedMaternityFile.presentPregnancy.lmp} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    lmp: e.target.value
                  }
                })} data-id="element-4184" data-mp="v9zmtsoma8qw" />
                      <Input label="Quickening" value={selectedMaternityFile.presentPregnancy.quickening} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    quickening: e.target.value
                  }
                })} data-id="element-4185" data-mp="2lbzlrkb59ap" />
                      <Input type="date" label="EDD" value={selectedMaternityFile.presentPregnancy.edd} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    edd: e.target.value
                  }
                })} data-id="element-4186" data-mp="b96h6cipqxxm" />
                      <Input label="Gestation Age (Weeks)" value={selectedMaternityFile.presentPregnancy.gestationAge} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    gestationAge: e.target.value
                  }
                })} data-id="element-4187" data-mp="2a0j0ktuqv4f4" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4188" data-mp="3gnod2x4lxofc">
                      <Input label="Summary of ANC Findings 1st BP" value={selectedMaternityFile.presentPregnancy.ancSummaryFirstBp} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    ancSummaryFirstBp: e.target.value
                  }
                })} data-id="element-4189" data-mp="3cml14lpokcje" />
                      <Input label="At Weeks" value={selectedMaternityFile.presentPregnancy.ancSummaryAtWeeks} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    ancSummaryAtWeeks: e.target.value
                  }
                })} data-id="element-4190" data-mp="33jz2ldtyv9fc" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4191" data-mp="303e16xz2dtsj">
                      <Input type="date" label="Syphilis Test Date" value={selectedMaternityFile.presentPregnancy.syphilisTest.date} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    syphilisTest: {
                      ...selectedMaternityFile.presentPregnancy.syphilisTest,
                      date: e.target.value
                    }
                  }
                })} data-id="element-4192" data-mp="2v3rp4ruz2jlb" />
                      <Input label="Result" value={selectedMaternityFile.presentPregnancy.syphilisTest.result} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    syphilisTest: {
                      ...selectedMaternityFile.presentPregnancy.syphilisTest,
                      result: e.target.value
                    }
                  }
                })} data-id="element-4193" data-mp="10wfof4naa43j" />
                      <Input label="Treatment" value={selectedMaternityFile.presentPregnancy.syphilisTest.treatment} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    syphilisTest: {
                      ...selectedMaternityFile.presentPregnancy.syphilisTest,
                      treatment: e.target.value
                    }
                  }
                })} data-id="element-4194" data-mp="321dhn2o259g8" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4195" data-mp="1j2ywlfxg01vf">
                      <Input type="date" label="HP Test Date" value={selectedMaternityFile.presentPregnancy.hpTest.date} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    hpTest: {
                      ...selectedMaternityFile.presentPregnancy.hpTest,
                      date: e.target.value
                    }
                  }
                })} data-id="element-4196" data-mp="1e3m7eoj3mipo" />
                      <Input label="Result" value={selectedMaternityFile.presentPregnancy.hpTest.result} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    hpTest: {
                      ...selectedMaternityFile.presentPregnancy.hpTest,
                      result: e.target.value
                    }
                  }
                })} data-id="element-4197" data-mp="7og220w4eq09" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4198" data-mp="1e6bn4bf3th6l">
                      <Input label="Pelvic Assessment" value={selectedMaternityFile.presentPregnancy.pelvicAssessment} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    pelvicAssessment: e.target.value
                  }
                })} data-id="element-4199" data-mp="2yw8kqoc3g0m3" />
                      <Input label="Abnormalities" value={selectedMaternityFile.presentPregnancy.abnormalities} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    abnormalities: e.target.value
                  }
                })} data-id="element-4200" data-mp="2ovoibicu8q2a" />
                    </div>
                  </Card>

                  <Card title="Observations for Latent Phase Labour" data-id="element-4201" data-mp="19dtm6gy55223">
                    <div className="overflow-x-auto" data-id="element-4202" data-mp="1dhhqmqrh7hj4">
                      <table className="w-full text-sm text-left" data-id="element-4203" data-mp="1igch8evoqads">
                        <thead className="bg-gray-50" data-id="element-4204" data-mp="1iehy963z4scc">
                          <tr data-id="element-4205" data-mp="1n8vxjjlrbjma">
                            <th className="p-2 border" data-id="element-4206" data-mp="1n9nrkpzph9ag">Date</th>
                            <th className="p-2 border" data-id="element-4207" data-mp="1s5ckwbl4ttyb">Time</th>
                            <th className="p-2 border" data-id="element-4208" data-mp="1x60mtsawfv7b">Pulse</th>
                            <th className="p-2 border" data-id="element-4209" data-mp="1x3nv4nfca6h2">BP</th>
                            <th className="p-2 border" data-id="element-4210" data-mp="218dp26qftazc">FH</th>
                            <th className="p-2 border" data-id="element-4211" data-mp="3lpegrszl4r3d">Temp</th>
                            <th className="p-2 border" data-id="element-4212" data-mp="3lqpmvzgi6axn">Urinalysis</th>
                            <th className="p-2 border" data-id="element-4213" data-mp="3hmq7xlavfk6e">Contractions</th>
                            <th className="p-2 border" data-id="element-4214" data-mp="3cogbl7deudj7">Cervical Dilation</th>
                            <th className="p-2 border" data-id="element-4215" data-mp="3cqjcimg3pr2p">Membranes Liquor</th>
                            <th className="p-2 border" data-id="element-4216" data-mp="37rg7v3419mv9">Remarks</th>
                            <th className="p-2 border" data-id="element-4217" data-mp="96y6h7p0vkmh">Sign</th>
                            <th className="p-2 border" data-id="element-4218" data-mp="48wn0lnopjah"></th>
                          </tr>
                        </thead>
                        <tbody data-id="element-4219" data-mp="3vn0ysoc6kyrk">
                          {selectedMaternityFile.labourObservations.map((obs, index) => <tr key={obs.id} data-id="element-4220" data-mp="3qmdawbj90y1k" data-mpi={index}>
                                <td className="p-1 border" data-id="element-4221" data-mp="ryqyayscuzsm">
                                  <input className="w-full p-1 border rounded" value={obs.date} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].date = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-4222" data-mp="s0aj92oyhz0q" />
                                </td>
                                <td className="p-1 border" data-id="element-4223" data-mp="1tpihktf4hrwi">
                                  <input className="w-full p-1 border rounded" value={obs.time} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].time = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-4224" data-mp="1ts4p4t20k7eq" />
                                </td>
                                <td className="p-1 border" data-id="element-4225" data-mp="tti47v0ykzpy">
                                  <input className="w-full p-1 border rounded" value={obs.pulse} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].pulse = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-4226" data-mp="tsyv93tadlo2" />
                                </td>
                                <td className="p-1 border" data-id="element-4227" data-mp="324owmh46k434">
                                  <input className="w-full p-1 border rounded" value={obs.bp} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].bp = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-4228" data-mp="324ys2k7zv3g7" />
                                </td>
                                <td className="p-1 border" data-id="element-4229" data-mp="mpv9ry92ulsr">
                                  <input className="w-full p-1 border rounded" value={obs.fh} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].fh = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-4230" data-mp="htefembabb78" />
                                </td>
                                <td className="p-1 border" data-id="element-4231" data-mp="2xb6m3esk90ik">
                                  <input className="w-full p-1 border rounded" value={obs.temp} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].temp = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-4232" data-mp="2sb0svu6n75yd" />
                                </td>
                                <td className="p-1 border" data-id="element-4233" data-mp="1bra0fy4zgbuy">
                                  <input className="w-full p-1 border rounded" value={obs.urinalysis} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].urinalysis = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-4234" data-mp="xqz96tzrgv99" />
                                </td>
                                <td className="p-1 border" data-id="element-4235" data-mp="2nzo5k5hhrp23">
                                  <input className="w-full p-1 border rounded" value={obs.contractions} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].contractions = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-4236" data-mp="2j132lyauur9y" />
                                </td>
                                <td className="p-1 border" data-id="element-4237" data-mp="3szvwmn6a3gsi">
                                  <input className="w-full p-1 border rounded" value={obs.cervicalDilation} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].cervicalDilation = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-4238" data-mp="3t1fbc9cadjs6" />
                                </td>
                                <td className="p-1 border" data-id="element-4239" data-mp="2vi43wlk0ac37">
                                  <input className="w-full p-1 border rounded" value={obs.membranesLiquor} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].membranesLiquor = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-4240" data-mp="114rz473g223o" />
                                </td>
                                <td className="p-1 border" data-id="element-4241" data-mp="3qlha3n5g01xt">
                                  <input className="w-full p-1 border rounded" value={obs.remarks} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].remarks = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-4242" data-mp="3qn25tg3htqx9" />
                                </td>
                                <td className="p-1 border" data-id="element-4243" data-mp="38oyrd604jwgz">
                                  <input className="w-full p-1 border rounded" value={obs.sign} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].sign = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-4244" data-mp="38nnqbdy2d2tk" />
                                </td>
                                <td className="p-1 border text-center" data-id="element-4245" data-mp="ui91gwzy7qem">
                                  <button onClick={() => {
                          const newObs = selectedMaternityFile.labourObservations.filter((_, i) => i !== index);
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} className="text-red-500 hover:text-red-700" data-id="element-4246" data-mp="ufwkon5xfm35">
                                    <Trash2 className="w-4 h-4" data-id="element-4247" data-mp="2ieqaid2rcsub" />
                                  </button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Button variant="outline" className="mt-4" onClick={() => setSelectedMaternityFile({
                ...selectedMaternityFile,
                labourObservations: [...selectedMaternityFile.labourObservations, {
                  id: Date.now().toString(),
                  date: '',
                  time: '',
                  pulse: '',
                  bp: '',
                  fh: '',
                  temp: '',
                  urinalysis: '',
                  contractions: '',
                  cervicalDilation: '',
                  membranesLiquor: '',
                  remarks: '',
                  sign: ''
                }]
              })} data-id="element-4248" data-mp="2w87t9vnme1ja">
                      + Add Observation
                    </Button>
                  </Card>

                  <Card title="Description of Labour and Delivery" data-id="element-4249" data-mp="vi1v505u0qy4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4250" data-mp="10a21pbgeqi3o">
                      <Input label="Laceration" value={selectedMaternityFile.delivery.laceration} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    laceration: e.target.value
                  }
                })} data-id="element-4251" data-mp="lknmvnu2b8gw" />
                      <Input label="Anterior" value={selectedMaternityFile.delivery.anterior} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    anterior: e.target.value
                  }
                })} data-id="element-4252" data-mp="198f3v4qjs9oi" />
                      <Input label="Posterior" value={selectedMaternityFile.delivery.posterior} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    posterior: e.target.value
                  }
                })} data-id="element-4253" data-mp="12f26fldtjbew" />
                      <Input label="None" value={selectedMaternityFile.delivery.none} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    none: e.target.value
                  }
                })} data-id="element-4254" data-mp="2dv0h1tona5i8" />
                      <Input label="Repaired" value={selectedMaternityFile.delivery.repaired} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    repaired: e.target.value
                  }
                })} data-id="element-4255" data-mp="3fnctb9i2w8hv" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4256" data-mp="3soujiklodt68">
                      <Input label="Membranes" value={selectedMaternityFile.delivery.membranes} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    membranes: e.target.value
                  }
                })} data-id="element-4257" data-mp="1j370o0tmf1w" />
                      <Input type="date" label="Date" value={selectedMaternityFile.delivery.membranesDate} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    membranesDate: e.target.value
                  }
                })} data-id="element-4258" data-mp="3ixzzlmvwj5ny" />
                      <Input type="time" label="Time" value={selectedMaternityFile.delivery.membranesTime} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    membranesTime: e.target.value
                  }
                })} data-id="element-4259" data-mp="2dunw5kjhxbvs" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4260" data-mp="1h20st1xtjfmr">
                      <Input label="Episiotomy" value={selectedMaternityFile.delivery.episiotomy} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    episiotomy: e.target.value
                  }
                })} data-id="element-4261" data-mp="3h4s6u8dnr0o6" />
                      <Input label="Repaired" value={selectedMaternityFile.delivery.episiotomyRepaired} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    episiotomyRepaired: e.target.value
                  }
                })} data-id="element-4262" data-mp="32h75ok0cfbiw" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4263" data-mp="1akcxpnnfskut">
                      <Input label="Maternal ARV" value={selectedMaternityFile.delivery.maternalARV} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    maternalARV: e.target.value
                  }
                })} data-id="element-4264" data-mp="15tmr6739ybyj" />
                      <Input label="Which ones (NPV, AZT, Other)" value={selectedMaternityFile.delivery.arvTypes} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    arvTypes: e.target.value
                  }
                })} data-id="element-4265" data-mp="3vcesk8ac86xx" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4" data-id="element-4266" data-mp="2d54a1bo04g10">
                      <Input label="Uterine Massage" value={selectedMaternityFile.delivery.uterineMassage} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    uterineMassage: e.target.value
                  }
                })} data-id="element-4267" data-mp="2i48lk8yeepui" />
                      <Input label="Mother Already on HAART" value={selectedMaternityFile.delivery.motherOnHAART} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    motherOnHAART: e.target.value
                  }
                })} data-id="element-4268" data-mp="l6t4lzi14vqy" />
                      <Input label="Cytotec" value={selectedMaternityFile.delivery.cytotec} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    cytotec: e.target.value
                  }
                })} data-id="element-4269" data-mp="u795d17e1os4" />
                      <Input label="Syntometrine" value={selectedMaternityFile.delivery.syntometrine} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    syntometrine: e.target.value
                  }
                })} data-id="element-4270" data-mp="qaa3wfsziht2" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4271" data-mp="3uoibfkdwjz57">
                      <Input type="date" label="1st Stage Began Date" value={selectedMaternityFile.delivery.firstStageBeganDate} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    firstStageBeganDate: e.target.value
                  }
                })} data-id="element-4272" data-mp="3umfd8d2lizpe" />
                      <Input type="time" label="Time" value={selectedMaternityFile.delivery.firstStageBeganTime} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    firstStageBeganTime: e.target.value
                  }
                })} data-id="element-4273" data-mp="3d8e7tkg5om7o" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4274" data-mp="z43fjs5gzcmg">
                      <Input label="Placenta Delivery" value={selectedMaternityFile.delivery.placentaDelivery} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    placentaDelivery: e.target.value
                  }
                })} data-id="element-4275" data-mp="1wp7vl0rxbem2" />
                      <Input type="date" label="Delivered Date" value={selectedMaternityFile.delivery.placentaDeliveredDate} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    placentaDeliveredDate: e.target.value
                  }
                })} data-id="element-4276" data-mp="2izhbuulj2z55" />
                      <Input type="time" label="Time" value={selectedMaternityFile.delivery.placentaDeliveredTime} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    placentaDeliveredTime: e.target.value
                  }
                })} data-id="element-4277" data-mp="3foowm0ebp480" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4278" data-mp="37kfsv22wq9qp">
                      <Input type="date" label="Complete Dilation Date" value={selectedMaternityFile.delivery.completeDilationDate} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    completeDilationDate: e.target.value
                  }
                })} data-id="element-4279" data-mp="37ictvsjs169u" />
                      <Input type="time" label="Time" value={selectedMaternityFile.delivery.completeDilationTime} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    completeDilationTime: e.target.value
                  }
                })} data-id="element-4280" data-mp="dpic7tqmtrhj" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4281" data-mp="339bw2kh1jb8o">
                      <Input label="Abnormality at Dilation" value={selectedMaternityFile.delivery.abnormalityAtDilation} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    abnormalityAtDilation: e.target.value
                  }
                })} data-id="element-4282" data-mp="379m3m1khe9pn" />
                      <Input label="Describe" value={selectedMaternityFile.delivery.abnormalityAtDilationDesc} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    abnormalityAtDilationDesc: e.target.value
                  }
                })} data-id="element-4283" data-mp="27i06kre3rd6h" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4284" data-mp="mzy2mxc7d4j5">
                      <Input label="Postpartum Hemorrhage" value={selectedMaternityFile.delivery.postpartumHemorrhage} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    postpartumHemorrhage: e.target.value
                  }
                })} data-id="element-4285" data-mp="3mfck7a0nd2e8" />
                      <Input label="Delivery Method" value={selectedMaternityFile.delivery.deliveryMethod} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    deliveryMethod: e.target.value
                  }
                })} data-id="element-4286" data-mp="pzg474w4lwn9" />
                      <Input label="EBL (cc)" value={selectedMaternityFile.delivery.ebl} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    ebl: e.target.value
                  }
                })} data-id="element-4287" data-mp="h0b59xgnk1ak" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4288" data-mp="1l9upx3bd65hv">
                      <Input label="Abnormality at Delivery" value={selectedMaternityFile.delivery.abnormalityAtDelivery} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    abnormalityAtDelivery: e.target.value
                  }
                })} data-id="element-4289" data-mp="1l805dq408feb" />
                      <Input label="Describe" value={selectedMaternityFile.delivery.abnormalityAtDeliveryDesc} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    abnormalityAtDeliveryDesc: e.target.value
                  }
                })} data-id="element-4290" data-mp="xlbhmnsbdsps" />
                      <Input label="Complications" value={selectedMaternityFile.delivery.complications} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    complications: e.target.value
                  }
                })} data-id="element-4291" data-mp="1z5hkupnak8nd" />
                    </div>
                    <h4 className="font-medium text-gray-900 mt-6 mb-3 border-b pb-1" data-id="element-4292" data-mp="344eia0cck90e">
                      Delivery Summary
                    </h4>
                    <div className="overflow-x-auto" data-id="element-4293" data-mp="387uamkhdvn7a">
                      <table className="w-full text-sm text-left" data-id="element-4294" data-mp="3d6dzotr9xf3v">
                        <thead className="bg-gray-50" data-id="element-4295" data-mp="3i4pbha14pzoq">
                          <tr data-id="element-4296" data-mp="3i34g5i6zqcpt">
                            <th className="p-2 border" data-id="element-4297" data-mp="3i2cj02xqw6xf">Date</th>
                            <th className="p-2 border" data-id="element-4298" data-mp="3mwqm6rinficn">Time of Birth</th>
                            <th className="p-2 border" data-id="element-4299" data-mp="3rus0kz54qths">Sex</th>
                            <th className="p-2 border" data-id="element-4300" data-mp="3rwbhmrym18km">Weight</th>
                            <th className="p-2 border" data-id="element-4301" data-mp="3vxop16hcpjtn">Delivered By</th>
                            <th className="p-2 border" data-id="element-4302" data-mp="4p15mw2o86oq"></th>
                          </tr>
                        </thead>
                        <tbody data-id="element-4303" data-mp="3sj3az9sb71g6">
                          {selectedMaternityFile.delivery.deliverySummary.map((sum, index) => <tr key={sum.id} data-id="element-4304" data-mp="638wbn93kwnc" data-mpi={index}>
                                <td className="p-1 border" data-id="element-4305" data-mp="641va21tehux">
                                  <Input type="date" value={sum.date} onChange={e => {
                          const newSum = [...selectedMaternityFile.delivery.deliverySummary];
                          newSum[index].date = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            delivery: {
                              ...selectedMaternityFile.delivery,
                              deliverySummary: newSum
                            }
                          });
                        }} data-id="element-4306" data-mp="1bqu6vvc8q0s" />
                                </td>
                                <td className="p-1 border" data-id="element-4307" data-mp="wjt2mufrjcoo">
                                  <Input type="time" value={sum.timeOfBirth} onChange={e => {
                          const newSum = [...selectedMaternityFile.delivery.deliverySummary];
                          newSum[index].timeOfBirth = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            delivery: {
                              ...selectedMaternityFile.delivery,
                              deliverySummary: newSum
                            }
                          });
                        }} data-id="element-4308" data-mp="ill3qgz621av" />
                                </td>
                                <td className="p-1 border" data-id="element-4309" data-mp="2p8ab81uqd60f">
                                  <Input value={sum.sex} onChange={e => {
                          const newSum = [...selectedMaternityFile.delivery.deliverySummary];
                          newSum[index].sex = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            delivery: {
                              ...selectedMaternityFile.delivery,
                              deliverySummary: newSum
                            }
                          });
                        }} data-id="element-4310" data-mp="2kasc136im55m" />
                                </td>
                                <td className="p-1 border" data-id="element-4311" data-mp="3uf22pwisfj65">
                                  <Input value={sum.weight} onChange={e => {
                          const newSum = [...selectedMaternityFile.delivery.deliverySummary];
                          newSum[index].weight = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            delivery: {
                              ...selectedMaternityFile.delivery,
                              deliverySummary: newSum
                            }
                          });
                        }} data-id="element-4312" data-mp="2brebmb28d0k" />
                                </td>
                                <td className="p-1 border" data-id="element-4313" data-mp="uvapuk6yg1ew">
                                  <Input value={sum.deliveredBy} onChange={e => {
                          const newSum = [...selectedMaternityFile.delivery.deliverySummary];
                          newSum[index].deliveredBy = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            delivery: {
                              ...selectedMaternityFile.delivery,
                              deliverySummary: newSum
                            }
                          });
                        }} data-id="element-4314" data-mp="uwu64asm2cgo" />
                                </td>
                                <td className="p-1 border text-center" data-id="element-4315" data-mp="3bj0jad3ffv27">
                                  <button onClick={() => {
                          const newSum = selectedMaternityFile.delivery.deliverySummary.filter((_, i) => i !== index);
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            delivery: {
                              ...selectedMaternityFile.delivery,
                              deliverySummary: newSum
                            }
                          });
                        }} className="text-red-500 hover:text-red-700" data-id="element-4316" data-mp="2wr9v30pe3jdz">
                                    <Trash2 className="w-4 h-4" data-id="element-4317" data-mp="3gcigx425dad9" />
                                  </button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Button variant="outline" className="mt-4" onClick={() => setSelectedMaternityFile({
                ...selectedMaternityFile,
                delivery: {
                  ...selectedMaternityFile.delivery,
                  deliverySummary: [...selectedMaternityFile.delivery.deliverySummary, {
                    id: Date.now().toString(),
                    date: '',
                    timeOfBirth: '',
                    sex: '',
                    weight: '',
                    deliveredBy: ''
                  }]
                }
              })} data-id="element-4318" data-mp="32fwi9qugjhhs">
                      + Add Baby
                    </Button>
                  </Card>

                  <Card title="Newborn Evaluation" data-id="element-4319" data-mp="fgheuykvirhv">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4" data-id="element-4320" data-mp="ff6d14azntte">
                      <Input label="EGA" value={selectedMaternityFile.newbornEvaluation.ega} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    ega: e.target.value
                  }
                })} data-id="element-4321" data-mp="34zuq6r9rqvox" />
                      <Input label="Alive" value={selectedMaternityFile.newbornEvaluation.alive} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    alive: e.target.value
                  }
                })} data-id="element-4322" data-mp="1hkn5a4b9syr" />
                      <Input label="Neonatal Death" value={selectedMaternityFile.newbornEvaluation.neonatalDeath} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    neonatalDeath: e.target.value
                  }
                })} data-id="element-4323" data-mp="g6dxyeysk7fg" />
                      <Input label="Cause of Death" value={selectedMaternityFile.newbornEvaluation.causeOfDeath} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    causeOfDeath: e.target.value
                  }
                })} data-id="element-4324" data-mp="3joe7hyjzwkv6" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4325" data-mp="dogw89gzxnuh">
                      <Input label="Apgar 1 Min" value={selectedMaternityFile.newbornEvaluation.apgar1} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    apgar1: e.target.value
                  }
                })} data-id="element-4326" data-mp="ii1nzjrzxll8" />
                      <Input label="Apgar 5 Min" value={selectedMaternityFile.newbornEvaluation.apgar5} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    apgar5: e.target.value
                  }
                })} data-id="element-4327" data-mp="wlilvtg4q1az" />
                      <Input label="Apgar 10 Min" value={selectedMaternityFile.newbornEvaluation.apgar10} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    apgar10: e.target.value
                  }
                })} data-id="element-4328" data-mp="281z4fvmzcy22" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4329" data-mp="3er1shml6rahb">
                      <Input label="General Exam" value={selectedMaternityFile.newbornEvaluation.generalExam} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    generalExam: e.target.value
                  }
                })} data-id="element-4330" data-mp="3jrpkaah8nvcl" />
                      <Input label="Congenital Malformation" value={selectedMaternityFile.newbornEvaluation.congenitalMalformation} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    congenitalMalformation: e.target.value
                  }
                })} data-id="element-4331" data-mp="2nmebdau5gqm6" />
                      <Input label="Describe" value={selectedMaternityFile.newbornEvaluation.congenitalMalformationDesc} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    congenitalMalformationDesc: e.target.value
                  }
                })} data-id="element-4332" data-mp="1qw5dlv0s3oyy" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4333" data-mp="1ejjgdtvf3ni7">
                      <Input label="Infant ARVs" value={selectedMaternityFile.newbornEvaluation.infantARVs} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    infantARVs: e.target.value
                  }
                })} data-id="element-4334" data-mp="1jat58s956mt1" />
                      <Input label="Infant Feeding" value={selectedMaternityFile.newbornEvaluation.infantFeeding} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    infantFeeding: e.target.value
                  }
                })} data-id="element-4335" data-mp="2q47d4w2zlnqb" />
                      <Input label="Resuscitation Methods" value={selectedMaternityFile.newbornEvaluation.resuscitationMethods} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    resuscitationMethods: e.target.value
                  }
                })} data-id="element-4336" data-mp="1nspk14fl5esh" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4337" data-mp="21rfd88zaaubk">
                      <Input label="Crown Heel Length" value={selectedMaternityFile.newbornEvaluation.crownHeelLength} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    crownHeelLength: e.target.value
                  }
                })} data-id="element-4338" data-mp="2phjfabwtqm0g" />
                      <Input label="Head Circumference" value={selectedMaternityFile.newbornEvaluation.headCircumference} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    headCircumference: e.target.value
                  }
                })} data-id="element-4339" data-mp="3r1hpairer26k" />
                    </div>
                    <h4 className="font-medium text-gray-900 mt-6 mb-3 border-b pb-1" data-id="element-4340" data-mp="3jp2038cmizu2">
                      Physical Examination
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4341" data-mp="3oimrghjpovka">
                      <Input label="Skin Jaundice" value={selectedMaternityFile.newbornEvaluation.skinJaundice} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    skinJaundice: e.target.value
                  }
                })} data-id="element-4342" data-mp="3tfms2cxsl640" />
                      <Input label="Skin Other" value={selectedMaternityFile.newbornEvaluation.skinOther} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    skinOther: e.target.value
                  }
                })} data-id="element-4343" data-mp="20r5dmow58ikc" />
                      <Input label="Fontanelles" value={selectedMaternityFile.newbornEvaluation.fontanelles} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    fontanelles: e.target.value
                  }
                })} data-id="element-4344" data-mp="29jh0y1vynpw3" />
                      <Input label="Eyes" value={selectedMaternityFile.newbornEvaluation.eyes} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    eyes: e.target.value
                  }
                })} data-id="element-4345" data-mp="gwgw616jzhr3" />
                      <Input label="Eyes Treatment" value={selectedMaternityFile.newbornEvaluation.eyesTreatment} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    eyesTreatment: e.target.value
                  }
                })} data-id="element-4346" data-mp="134cos77t4scn" />
                      <Input label="Mouth" value={selectedMaternityFile.newbornEvaluation.mouth} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    mouth: e.target.value
                  }
                })} data-id="element-4347" data-mp="1uw1z1i6xsa5e" />
                      <Input label="Neck" value={selectedMaternityFile.newbornEvaluation.neck} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    neck: e.target.value
                  }
                })} data-id="element-4348" data-mp="1ht94a522ldov" />
                      <Input label="Chest" value={selectedMaternityFile.newbornEvaluation.chest} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    chest: e.target.value
                  }
                })} data-id="element-4349" data-mp="2aglpobwhvfgl" />
                      <Input label="Abdomen" value={selectedMaternityFile.newbornEvaluation.abdomen} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    abdomen: e.target.value
                  }
                })} data-id="element-4350" data-mp="2k6agrw6fcdsp" />
                      <Input label="Umbilicus" value={selectedMaternityFile.newbornEvaluation.umbilicus} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    umbilicus: e.target.value
                  }
                })} data-id="element-4351" data-mp="1rhwidfbfbfgm" />
                      <Input label="Genitalia" value={selectedMaternityFile.newbornEvaluation.genitalia} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    genitalia: e.target.value
                  }
                })} data-id="element-4352" data-mp="rnesuwbyumge" />
                      <Input label="Urine" value={selectedMaternityFile.newbornEvaluation.urine} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    urine: e.target.value
                  }
                })} data-id="element-4353" data-mp="2ycyuj0gciqck" />
                      <Input label="Anus" value={selectedMaternityFile.newbornEvaluation.anus} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    anus: e.target.value
                  }
                })} data-id="element-4354" data-mp="3r02rqj9760ds" />
                      <Input label="Meconium" value={selectedMaternityFile.newbornEvaluation.meconium} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    meconium: e.target.value
                  }
                })} data-id="element-4355" data-mp="2nkitzh4z6mu0" />
                      <Input label="Upper Limbs" value={selectedMaternityFile.newbornEvaluation.upperLimbs} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    upperLimbs: e.target.value
                  }
                })} data-id="element-4356" data-mp="fdyrct9qol4c" />
                      <Input label="Erb Paralysis" value={selectedMaternityFile.newbornEvaluation.erbParalysis} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    erbParalysis: e.target.value
                  }
                })} data-id="element-4357" data-mp="p2ytgljjfwmr" />
                      <Input label="Lower Limbs" value={selectedMaternityFile.newbornEvaluation.lowerLimbs} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    lowerLimbs: e.target.value
                  }
                })} data-id="element-4358" data-mp="3rjnzxpbj7439" />
                      <Input label="Ortolani Sign" value={selectedMaternityFile.newbornEvaluation.ortolaniSign} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    ortolaniSign: e.target.value
                  }
                })} data-id="element-4359" data-mp="16zp1wnq1jjqb" />
                      <Input label="Back" value={selectedMaternityFile.newbornEvaluation.back} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    back: e.target.value
                  }
                })} data-id="element-4360" data-mp="270k7op3lucg9" />
                    </div>
                    <h4 className="font-medium text-gray-900 mt-6 mb-3 border-b pb-1" data-id="element-4361" data-mp="3dscmd6jg4ubd">
                      Reflexes
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4362" data-mp="3irfqmorlqwia">
                      <Input label="Grasp" value={selectedMaternityFile.newbornEvaluation.reflexesGrasp} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    reflexesGrasp: e.target.value
                  }
                })} data-id="element-4363" data-mp="acfc6xlabmd1" />
                      <Input label="Moro (Startle)" value={selectedMaternityFile.newbornEvaluation.reflexesMoro} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    reflexesMoro: e.target.value
                  }
                })} data-id="element-4364" data-mp="2ds0sa44xvtr0" />
                      <Input label="Sucking" value={selectedMaternityFile.newbornEvaluation.reflexesSucking} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    reflexesSucking: e.target.value
                  }
                })} data-id="element-4365" data-mp="23t1ujglppiis" />
                    </div>
                    <div className="mt-4" data-id="element-4366" data-mp="1u6nmb5g08hyj">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4367" data-mp="1y6o32wqkt58r">
                        Newborn Progress Notes
                      </label>
                      <textarea className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-pink-500 focus:outline-none" rows={4} value={selectedMaternityFile.newbornEvaluation.progressNotes} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    progressNotes: e.target.value
                  }
                })} data-id="element-4368" data-mp="234g20apn1rmg" />
                    </div>
                  </Card>

                  <Card title="Post Delivery Observation Chart" data-id="element-4369" data-mp="k7307fq70cug">
                    <div className="overflow-x-auto" data-id="element-4370" data-mp="k5i3bjgeohtf">
                      <table className="w-full text-sm text-left" data-id="element-4371" data-mp="f7goxbtxd6oa">
                        <thead className="bg-gray-50" data-id="element-4372" data-mp="f5ndlj8d9y9y">
                          <tr data-id="element-4373" data-mp="aa83919kikia">
                            <th className="p-2 border" colSpan={2} data-id="element-4374" data-mp="a6u1nv8qafbw">
                              Time
                            </th>
                            <th className="p-2 border text-center" colSpan={8} data-id="element-4375" data-mp="17p2qnt98jz7">
                              Mother
                            </th>
                            <th className="p-2 border text-center" colSpan={7} data-id="element-4376" data-mp="21b9s78izzip3">
                              Baby
                            </th>
                            <th className="p-2 border" rowSpan={2} data-id="element-4377" data-mp="1rl6qmjxt8wf5">
                              Sign
                            </th>
                            <th className="p-2 border" rowSpan={2} data-id="element-4378" data-mp="1wigl4e023y9u"></th>
                          </tr>
                          <tr data-id="element-4379" data-mp="1hkxpqssvimtf">
                            <th className="p-2 border" data-id="element-4380" data-mp="1hotwjkycsgjv">Date</th>
                            <th className="p-2 border" data-id="element-4381" data-mp="1mmdeovy69dh3">Time</th>
                            <th className="p-2 border" data-id="element-4382" data-mp="18mc9536yphva">BP</th>
                            <th className="p-2 border" data-id="element-4383" data-mp="18nvuh87h6j3x">Pulse</th>
                            <th className="p-2 border" data-id="element-4384" data-mp="1dlovznxu2oxv">Resps</th>
                            <th className="p-2 border" data-id="element-4385" data-mp="3303jk4x485rs">Temp</th>
                            <th className="p-2 border" data-id="element-4386" data-mp="32xr01nbplncm">PV Bleed</th>
                            <th className="p-2 border" data-id="element-4387" data-mp="37xltnymvcr1d">Uterus</th>
                            <th className="p-2 border" data-id="element-4388" data-mp="3i2byqddkkb9t">Bladder</th>
                            <th className="p-2 border" data-id="element-4389" data-mp="3i0r0ods1qa77">Pallor</th>
                            <th className="p-2 border" data-id="element-4390" data-mp="3d51ys3sk0h7q">Temp</th>
                            <th className="p-2 border" data-id="element-4391" data-mp="3qvozu2lsej6z">Resps</th>
                            <th className="p-2 border" data-id="element-4392" data-mp="3qx9vjvju886f">Cord</th>
                            <th className="p-2 border" data-id="element-4393" data-mp="3m3nezsajzk5c">Skin</th>
                            <th className="p-2 border" data-id="element-4394" data-mp="4np3k2s7t2r0">Feed BF/AF</th>
                            <th className="p-2 border" data-id="element-4395" data-mp="4q1tb247shem">Bowel</th>
                            <th className="p-2 border" data-id="element-4396" data-mp="3vtgpepyylt80">Urine</th>
                          </tr>
                        </thead>
                        <tbody data-id="element-4397" data-mp="dmj7ke7vkvel">
                          {selectedMaternityFile.postDeliveryObservations.map((obs, index) => <tr key={obs.id} data-id="element-4398" data-mp="2fh1ophxdiaoy" data-mpi={index}>
                                <td className="p-1 border" data-id="element-4399" data-mp="2fhtkotuvuafr">
                                  <input className="w-full p-1 border rounded" value={obs.date} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].date = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4400" data-mp="2bfq5pv908npq" />
                                </td>
                                <td className="p-1 border" data-id="element-4401" data-mp="1c3dm6pyoct6e">
                                  <input className="w-full p-1 border rounded" value={obs.time} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].time = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4402" data-mp="1h1xdl5w1ex65" />
                                </td>
                                <td className="p-1 border" data-id="element-4403" data-mp="3fg390lhff029">
                                  <input className="w-full p-1 border rounded" value={obs.bp} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].bp = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4404" data-mp="3aitkd81v2sfh" />
                                </td>
                                <td className="p-1 border" data-id="element-4405" data-mp="329ltxecwpcc">
                                  <input className="w-full p-1 border rounded" value={obs.pulse} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].pulse = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4406" data-mp="3l9qr25dv13r6" />
                                </td>
                                <td className="p-1 border" data-id="element-4407" data-mp="1inlfxlv1oymp">
                                  <input className="w-full p-1 border rounded" value={obs.resps} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].resps = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4408" data-mp="1ip6bnet3inm5" />
                                </td>
                                <td className="p-1 border" data-id="element-4409" data-mp="3dni7ddqejtqj">
                                  <input className="w-full p-1 border rounded" value={obs.temp} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].temp = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4410" data-mp="3dnrvswvstsu4" />
                                </td>
                                <td className="p-1 border" data-id="element-4411" data-mp="11e1txlbvb07h">
                                  <input className="w-full p-1 border rounded" value={obs.pvBleeding} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].pvBleeding = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4412" data-mp="11ejnga75b0bp" />
                                </td>
                                <td className="p-1 border" data-id="element-4413" data-mp="3c02ahbvut23n">
                                  <input className="w-full p-1 border rounded" value={obs.uterus} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].uterus = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4414" data-mp="3byhl00oec9cn" />
                                </td>
                                <td className="p-1 border" data-id="element-4415" data-mp="yt44vkdvigmt">
                                  <input className="w-full p-1 border rounded" value={obs.bladder} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].bladder = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4416" data-mp="yrjapvvh0zph" />
                                </td>
                                <td className="p-1 border" data-id="element-4417" data-mp="1yur34npqmqyt">
                                  <input className="w-full p-1 border rounded" value={obs.pallor} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].pallor = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4418" data-mp="1tumlgud3fq70" />
                                </td>
                                <td className="p-1 border" data-id="element-4419" data-mp="2wihqpvz318gn">
                                  <input className="w-full p-1 border rounded" value={obs.babyTemp} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].babyTemp = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4420" data-mp="3f608aak3i680" />
                                </td>
                                <td className="p-1 border" data-id="element-4421" data-mp="2dgwt9x8eno2s">
                                  <input className="w-full p-1 border rounded" value={obs.babyResps} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].babyResps = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4422" data-mp="2ig122mrf3jsl" />
                                </td>
                                <td className="p-1 border" data-id="element-4423" data-mp="sy462lu0afim">
                                  <input className="w-full p-1 border rounded" value={obs.cord} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].cord = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4424" data-mp="3iny0mmnsms0h" />
                                </td>
                                <td className="p-1 border" data-id="element-4425" data-mp="4pfpk0wunxv">
                                  <input className="w-full p-1 border rounded" value={obs.skinColour} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].skinColour = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4426" data-mp="53agbkk2r9mu" />
                                </td>
                                <td className="p-1 border" data-id="element-4427" data-mp="3kwblivpwr5by">
                                  <input className="w-full p-1 border rounded" value={obs.feedingBF} placeholder="BF" onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].feedingBF = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4428" data-mp="2p5dxrhlr7px" />
                                  <input className="w-full p-1 border rounded mt-1" value={obs.feedingAF} placeholder="AF" onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].feedingAF = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4429" data-mp="25bkg81x77q1o" />
                                </td>
                                <td className="p-1 border" data-id="element-4430" data-mp="373gsupnwkqjv">
                                  <input className="w-full p-1 border rounded" value={obs.eliminationBowel} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].eliminationBowel = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4431" data-mp="3751n0e6b27h7" />
                                </td>
                                <td className="p-1 border" data-id="element-4432" data-mp="2710tb0vumix4">
                                  <input className="w-full p-1 border rounded" value={obs.eliminationUrine} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].eliminationUrine = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4433" data-mp="224jzpr5vrccn" />
                                </td>
                                <td className="p-1 border" data-id="element-4434" data-mp="1bu9o910yj1zt">
                                  <input className="w-full p-1 border rounded" value={obs.sign} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].sign = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-4435" data-mp="1zit0frn4nyx6" />
                                </td>
                                <td className="p-1 border text-center" data-id="element-4436" data-mp="36d08gdlfer3m">
                                  <button onClick={() => {
                          const newObs = selectedMaternityFile.postDeliveryObservations.filter((_, i) => i !== index);
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} className="text-red-500 hover:text-red-700" data-id="element-4437" data-mp="32cztmruf0dw1">
                                    <Trash2 className="w-4 h-4" data-id="element-4438" data-mp="23k63v2zgp679" />
                                  </button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Button variant="outline" className="mt-4" onClick={() => setSelectedMaternityFile({
                ...selectedMaternityFile,
                postDeliveryObservations: [...selectedMaternityFile.postDeliveryObservations, {
                  id: Date.now().toString(),
                  date: '',
                  time: '',
                  bp: '',
                  pulse: '',
                  resps: '',
                  temp: '',
                  pvBleeding: '',
                  uterus: '',
                  bladder: '',
                  pallor: '',
                  babyTemp: '',
                  babyResps: '',
                  cord: '',
                  skinColour: '',
                  feedingBF: '',
                  feedingAF: '',
                  eliminationBowel: '',
                  eliminationUrine: '',
                  sign: ''
                }]
              })} data-id="element-4439" data-mp="2na9qpj22wio1">
                      + Add Observation
                    </Button>
                  </Card>

                  <Card title="Drug Chart" data-id="element-4440" data-mp="3o3s05m0algcq">
                    <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-4441" data-mp="3o2qcmi23b1e4">
                      <Input label="Ward" value={selectedMaternityFile?.drugChart?.ward || ''} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile!,
                  drugChart: {
                    ...(selectedMaternityFile?.drugChart || {
                      ward: '',
                      patientNo: '',
                      supplyDates: ['', '', ''],
                      rows: [],
                      signedBy: '',
                      signedDate: ''
                    }),
                    ward: e.target.value
                  }
                })} data-id="element-4442" data-mp="2g5nnxoudty1p" />
                      <Input label="Patient No." value={selectedMaternityFile?.drugChart?.patientNo || ''} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile!,
                  drugChart: {
                    ...(selectedMaternityFile?.drugChart || {
                      ward: '',
                      patientNo: '',
                      supplyDates: ['', '', ''],
                      rows: [],
                      signedBy: '',
                      signedDate: ''
                    }),
                    patientNo: e.target.value
                  }
                })} data-id="element-4443" data-mp="1ih3qv7up2cy1" />
                    </div>
                    <div className="overflow-x-auto" data-id="element-4444" data-mp="1wj2j9kfp5ne6">
                      <table className="w-full text-xs text-center border-collapse border border-gray-300 min-w-[1200px]" data-id="element-4445" data-mp="1wkdo7nkvp16v">
                        <thead className="bg-gray-50" data-id="element-4446" data-mp="h8ugef25rxlr">
                          <tr data-id="element-4447" data-mp="m4jgqkm05ij4">
                            <th className="p-1 border border-gray-300 w-24" rowSpan={3} data-id="element-4448" data-mp="m7nx0gcqs4q5">
                              Date
                            </th>
                            <th className="p-1 border border-gray-300 w-32" rowSpan={3} data-id="element-4449" data-mp="3ulwfgvljvf4l">
                              Drug
                            </th>
                            <th className="p-1 border border-gray-300 w-20" rowSpan={3} data-id="element-4450" data-mp="8cva2wuivm9j">
                              Dosage
                            </th>
                            <th className="p-1 border border-gray-300 w-20" rowSpan={3} data-id="element-4451" data-mp="3gtg8jjpj2wye">
                              Route of Admin
                            </th>
                            <th className="p-1 border border-gray-300 w-24" rowSpan={3} data-id="element-4452" data-mp="1tv5o2s6v5q4s">
                              Ordered By
                            </th>
                            <th className="p-1 border border-gray-300" colSpan={21} data-id="element-4453" data-mp="1f0981sstbyh5">
                              SUPPLIED BY (DATE)
                            </th>
                            <th className="p-1 border border-gray-300 w-24" rowSpan={3} data-id="element-4454" data-mp="165bcedya67m4">
                              Supplied By
                            </th>
                            <th className="p-1 border border-gray-300 w-20" rowSpan={3} data-id="element-4455" data-mp="rbga5qsmrgjw">
                              Quantity
                            </th>
                            <th className="p-1 border border-gray-300 w-24" rowSpan={3} data-id="element-4456" data-mp="l1610aa5pymy">
                              Date
                            </th>
                          </tr>
                          <tr data-id="element-4457" data-mp="74ir76x3vdzm">
                            {[0, 1, 2].map(colIdx => <th key={colIdx} className="p-1 border border-gray-300" colSpan={7} data-id="element-4458" data-mp="2awp28okisi2">
                                <input type="date" className="w-full border-gray-300 rounded-sm text-xs" value={selectedMaternityFile?.drugChart?.supplyDates?.[colIdx] || ''} onChange={e => {
                          const newDates = [...(selectedMaternityFile?.drugChart?.supplyDates || ['', '', ''])] as [string, string, string];
                          newDates[colIdx] = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...(selectedMaternityFile!.drugChart || {
                                ward: '',
                                patientNo: '',
                                supplyDates: ['', '', ''],
                                rows: [],
                                signedBy: '',
                                signedDate: ''
                              }),
                              supplyDates: newDates
                            }
                          });
                        }} data-id="element-4459" data-mp="3ohux12c45pyd" />
                              </th>)}
                          </tr>
                          <tr data-id="element-4460" data-mp="1d0e0gk5hew03">
                            {[0, 1, 2].map(colIdx => <Fragment key={`times-${colIdx}`} data-id="element-4461" data-mp="1qzvv3g5gl3hd">
                                {['6', '10', '12', '14', '18', '22', '24'].map(time => <th key={`${colIdx}-${time}`} className="p-1 border border-gray-300 w-8 font-normal" data-id="element-4462" data-mp="32bwmz3xmd81c">
                                      {time}
                                    </th>)}
                              </Fragment>)}
                          </tr>
                        </thead>
                        <tbody data-id="element-4463" data-mp="30yeuu58t0zof">
                          {(selectedMaternityFile?.drugChart?.rows || []).map((row, rowIndex) => <tr key={row.id} data-id="element-4464" data-mp="3tjogh0c9xty8" data-mpi={rowIndex}>
                                <td className="p-0 border border-gray-300" data-id="element-4465" data-mp="3ew3ej9r4y0rw">
                                  <input type="date" className="w-full border-0 text-xs p-1" value={row.date} onChange={e => {
                          const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                          newRows[rowIndex].date = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...selectedMaternityFile!.drugChart!,
                              rows: newRows
                            }
                          });
                        }} data-id="element-4466" data-mp="3evtq3qlqo1ob" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-4467" data-mp="2sohhofubn7qa">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.drug} onChange={e => {
                          const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                          newRows[rowIndex].drug = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...selectedMaternityFile!.drugChart!,
                              rows: newRows
                            }
                          });
                        }} data-id="element-4468" data-mp="3q4wy6z968r0b" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-4469" data-mp="138f85xnty3mi">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.dosage} onChange={e => {
                          const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                          newRows[rowIndex].dosage = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...selectedMaternityFile!.drugChart!,
                              rows: newRows
                            }
                          });
                        }} data-id="element-4470" data-mp="307gomye09kku" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-4471" data-mp="1nhq0pf3w4cyt">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.routeOfAdmin} onChange={e => {
                          const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                          newRows[rowIndex].routeOfAdmin = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...selectedMaternityFile!.drugChart!,
                              rows: newRows
                            }
                          });
                        }} data-id="element-4472" data-mp="19qbe6yarxtn0" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-4473" data-mp="3kwu6o7z1vo5e">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.orderedBy} onChange={e => {
                          const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                          newRows[rowIndex].orderedBy = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...selectedMaternityFile!.drugChart!,
                              rows: newRows
                            }
                          });
                        }} data-id="element-4474" data-mp="3fzahwe4q7sz7" />
                                </td>
                                {[0, 1, 2].map(colIdx => <Fragment key={`admin-${colIdx}`} data-id="element-4475" data-mp="285se6mno14r7">
                                    {['6', '10', '12', '14', '18', '22', '24'].map(time => {
                          const key = `${colIdx}-${time}`;
                          return <td key={key} className="p-0 border border-gray-300" data-id="element-4476" data-mp="9qmk995jyjjl">
                                          <input type="text" className="w-full border-0 text-xs p-1 text-center" value={row.administration?.[key] || ''} onChange={e => {
                              const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                              newRows[rowIndex].administration = {
                                ...newRows[rowIndex].administration,
                                [key]: e.target.value
                              };
                              setSelectedMaternityFile({
                                ...selectedMaternityFile!,
                                drugChart: {
                                  ...selectedMaternityFile!.drugChart!,
                                  rows: newRows
                                }
                              });
                            }} data-id="element-4477" data-mp="pfzuozs8ol2" />
                                        </td>;
                        })}
                                  </Fragment>)}
                                <td className="p-0 border border-gray-300" data-id="element-4478" data-mp="37ohnzzsckle0">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.suppliedBy} onChange={e => {
                          const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                          newRows[rowIndex].suppliedBy = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...selectedMaternityFile!.drugChart!,
                              rows: newRows
                            }
                          });
                        }} data-id="element-4479" data-mp="3clhv8e0xnu6p" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-4480" data-mp="1xp6u84b1yzis">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.quantity} onChange={e => {
                          const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                          newRows[rowIndex].quantity = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...selectedMaternityFile!.drugChart!,
                              rows: newRows
                            }
                          });
                        }} data-id="element-4481" data-mp="1xndjactept4z" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-4482" data-mp="37llg0aq2n05i">
                                  <input type="date" className="w-full border-0 text-xs p-1" value={row.suppliedDate} onChange={e => {
                          const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                          newRows[rowIndex].suppliedDate = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...selectedMaternityFile!.drugChart!,
                              rows: newRows
                            }
                          });
                        }} data-id="element-4483" data-mp="3ccts5eisczmd" />
                                </td>
                                <td className="p-1 border border-gray-300" data-id="element-4484" data-mp="v1jl4vadmlpq">
                                  <button type="button" onClick={() => {
                          const newRows = selectedMaternityFile!.drugChart!.rows.filter((_, i) => i !== rowIndex);
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...selectedMaternityFile!.drugChart!,
                              rows: newRows
                            }
                          });
                        }} className="text-red-500 hover:text-red-700" data-id="element-4485" data-mp="100mombapkjvl">
                                    <Trash2 className="w-4 h-4" data-id="element-4486" data-mp="3m6g3jnr14vjy" />
                                  </button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Button variant="outline" className="mt-4" onClick={() => {
                const currentChart = selectedMaternityFile?.drugChart || {
                  ward: '',
                  patientNo: '',
                  supplyDates: ['', '', ''],
                  rows: [],
                  signedBy: '',
                  signedDate: ''
                };
                setSelectedMaternityFile({
                  ...selectedMaternityFile!,
                  drugChart: {
                    ...currentChart,
                    rows: [...currentChart.rows, {
                      id: `drug-${Date.now()}`,
                      date: '',
                      drug: '',
                      dosage: '',
                      routeOfAdmin: '',
                      orderedBy: '',
                      administration: {},
                      suppliedBy: '',
                      quantity: '',
                      suppliedDate: ''
                    }]
                  }
                });
              }} data-id="element-4487" data-mp="339oesyspxo8i">
                      + Add Drug Row
                    </Button>
                    <div className="grid grid-cols-2 gap-4 mt-6" data-id="element-4488" data-mp="36svlf7n1h7ad">
                      <Input label="Date" type="date" value={selectedMaternityFile?.drugChart?.signedDate || ''} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile!,
                  drugChart: {
                    ...(selectedMaternityFile?.drugChart || {
                      ward: '',
                      patientNo: '',
                      supplyDates: ['', '', ''],
                      rows: [],
                      signedBy: '',
                      signedDate: ''
                    }),
                    signedDate: e.target.value
                  }
                })} data-id="element-4489" data-mp="2n628jon6efhz" />
                      <Input label="Signature" value={selectedMaternityFile?.drugChart?.signedBy || ''} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile!,
                  drugChart: {
                    ...(selectedMaternityFile?.drugChart || {
                      ward: '',
                      patientNo: '',
                      supplyDates: ['', '', ''],
                      rows: [],
                      signedBy: '',
                      signedDate: ''
                    }),
                    signedBy: e.target.value
                  }
                })} data-id="element-4490" data-mp="gzospu4ksvks" />
                    </div>
                  </Card>

                  <Card title="Puerperium Notes" data-id="element-4491" data-mp="1em49sut3br7t">
                    <div className="overflow-x-auto" data-id="element-4492" data-mp="1y9r0fyqby8ru">
                      <table className="w-full text-sm text-left" data-id="element-4493" data-mp="1y863y3kggfrc">
                        <thead className="bg-gray-50" data-id="element-4494" data-mp="22b2fxbfhpdmb">
                          <tr data-id="element-4495" data-mp="1oc310vhs4d3c">
                            <th className="p-2 border w-32" data-id="element-4496" data-mp="1ob1ffx35087d">Date</th>
                            <th className="p-2 border" data-id="element-4497" data-mp="1taedldzgl1p9">Notes</th>
                            <th className="p-2 border w-48" data-id="element-4498" data-mp="1td0jl96pb95d">Signature</th>
                            <th className="p-2 border w-12" data-id="element-4499" data-mp="1ekgkcjmo9lt7"></th>
                          </tr>
                        </thead>
                        <tbody data-id="element-4500" data-mp="1jeugio95rwyx">
                          {selectedMaternityFile.puerperiumNotes.map((note, index) => <tr key={note.id} data-id="element-4501" data-mp="1alhix905g76i" data-mpi={index}>
                                <td className="p-1 border" data-id="element-4502" data-mp="1ai1zjtzg0bza">
                                  <Input type="date" value={note.date} onChange={e => {
                          const newNotes = [...selectedMaternityFile.puerperiumNotes];
                          newNotes[index].date = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            puerperiumNotes: newNotes
                          });
                        }} data-id="element-4503" data-mp="vogo2wmnvw3t" />
                                </td>
                                <td className="p-1 border" data-id="element-4504" data-mp="qgqp2sst9emh">
                                  <textarea className="w-full p-1 border rounded" rows={2} value={note.notes} onChange={e => {
                          const newNotes = [...selectedMaternityFile.puerperiumNotes];
                          newNotes[index].notes = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            puerperiumNotes: newNotes
                          });
                        }} data-id="element-4505" data-mp="ljqhuek865ts" />
                                </td>
                                <td className="p-1 border" data-id="element-4506" data-mp="2eg2vf0zqfwd0">
                                  <Input value={note.signature} onChange={e => {
                          const newNotes = [...selectedMaternityFile.puerperiumNotes];
                          newNotes[index].signature = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            puerperiumNotes: newNotes
                          });
                        }} data-id="element-4507" data-mp="2egm19jc3yuao" />
                                </td>
                                <td className="p-1 border text-center" data-id="element-4508" data-mp="1wqz3awl00sw8">
                                  <button onClick={() => {
                          const newNotes = selectedMaternityFile.puerperiumNotes.filter((_, i) => i !== index);
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            puerperiumNotes: newNotes
                          });
                        }} className="text-red-500 hover:text-red-700" data-id="element-4509" data-mp="1rsnv0qacqqg4">
                                    <Trash2 className="w-4 h-4" data-id="element-4510" data-mp="p14k5erengqt" />
                                  </button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Button variant="outline" className="mt-4" onClick={() => setSelectedMaternityFile({
                ...selectedMaternityFile,
                puerperiumNotes: [...selectedMaternityFile.puerperiumNotes, {
                  id: Date.now().toString(),
                  date: '',
                  notes: '',
                  signature: ''
                }]
              })} data-id="element-4511" data-mp="1mnbn6k829112">
                      + Add Note
                    </Button>
                  </Card>

                  <Card title="Discharge Slip" data-id="element-4512" data-mp="1p7gl1g666pwm">
                    {(() => {
                const defaultSlip = {
                  smNo: '',
                  gravida: '',
                  patientName: '',
                  party: '',
                  timeOfDischarge: '',
                  age: '',
                  motherDob: '',
                  modeOfDelivery: '',
                  perineum: '',
                  comments: '',
                  pca: '',
                  infantTob: '',
                  apgarScore: '',
                  infantSex: '',
                  dateOfDischarge: '',
                  mother: {
                    bp: '',
                    pallor: '',
                    breast: '',
                    breastfeeding: '',
                    uterus: '',
                    perineum: '',
                    lochia: '',
                    bowels: '',
                    urine: '',
                    familyPlanning: '',
                    dangerSigns: '',
                    treatment: '',
                    vitaminA: ''
                  },
                  infant: {
                    birthWeight: '',
                    dischargeWeight: '',
                    pallor: '',
                    jaundice: '',
                    passedMeconiumUrine: '',
                    feeding: '',
                    feedingOption: '',
                    breastFeeding: '',
                    bcg: '',
                    opv: '',
                    birthRecord: '',
                    gc: '',
                    treatment: ''
                  },
                  sixDaysPostnatalDate: '',
                  sixWeeksPostnatalDate: '',
                  nameInFull: ''
                };
                const slip = selectedMaternityFile.dischargeSlip || defaultSlip;
                const updateSlip = (patch: Partial<typeof defaultSlip>) => {
                  setSelectedMaternityFile({
                    ...selectedMaternityFile,
                    dischargeSlip: {
                      ...slip,
                      ...patch
                    }
                  });
                };
                const updateMother = (patch: Partial<typeof defaultSlip.mother>) => {
                  updateSlip({
                    mother: {
                      ...slip.mother,
                      ...patch
                    }
                  });
                };
                const updateInfant = (patch: Partial<typeof defaultSlip.infant>) => {
                  updateSlip({
                    infant: {
                      ...slip.infant,
                      ...patch
                    }
                  });
                };
                return <div className="space-y-6" data-id="element-4513" data-mp="1lrouzmhvy44m">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4514" data-mp="1lukuvgdan2vp">
                            <Input label="SM No" value={slip.smNo} onChange={e => updateSlip({
                      smNo: e.target.value
                    })} data-id="element-4515" data-mp="3b87sp35ntg79" />
                            <Input label="Gravida" value={slip.gravida} onChange={e => updateSlip({
                      gravida: e.target.value
                    })} data-id="element-4516" data-mp="u9e631m0pemf" />
                            <Input label="Patient's Name" value={slip.patientName} onChange={e => updateSlip({
                      patientName: e.target.value
                    })} data-id="element-4517" data-mp="1d0f4noj8785g" />
                            <Input label="Party" value={slip.party} onChange={e => updateSlip({
                      party: e.target.value
                    })} data-id="element-4518" data-mp="3c8dnkdx97osr" />
                            <Input label="Time of Discharge" type="time" value={slip.timeOfDischarge} onChange={e => updateSlip({
                      timeOfDischarge: e.target.value
                    })} data-id="element-4519" data-mp="2xh4rpn2rd776" />
                            <Input label="Age" value={slip.age} onChange={e => updateSlip({
                      age: e.target.value
                    })} data-id="element-4520" data-mp="29ug36ij8ughl" />
                          </div>

                          <div data-id="element-4521" data-mp="1x9rb33w18ndb">
                            <h4 className="font-semibold text-sm uppercase mb-2 border-b pb-1" data-id="element-4522" data-mp="1xaj2e2iljyxs">
                              Labour and Delivery Summary
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4523" data-mp="1ogwfz30a4819">
                              <div data-id="element-4524" data-mp="1t9zapegslnj1">
                                <h5 className="font-semibold mb-2 text-pink-600" data-id="element-4525" data-mp="1tb0lrixt6a0r">
                                  Mother
                                </h5>
                                <div className="space-y-3" data-id="element-4526" data-mp="1jk5xk91050co">
                                  <Input label="D.O.B" type="date" value={slip.motherDob} onChange={e => updateSlip({
                            motherDob: e.target.value
                          })} data-id="element-4527" data-mp="15jv7v9bfhrt3" />
                                  <Input label="M.O.D (Mode of Delivery)" value={slip.modeOfDelivery} onChange={e => updateSlip({
                            modeOfDelivery: e.target.value
                          })} data-id="element-4528" data-mp="zv87til9hz7v" />
                                  <Input label="Perineum" value={slip.perineum} onChange={e => updateSlip({
                            perineum: e.target.value
                          })} data-id="element-4529" data-mp="1en0j9jhxqt4b" />
                                  <div data-id="element-4530" data-mp="2ghlf7zc56b3j">
                                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4531" data-mp="2gimtecogfdph">
                                      Comments
                                    </label>
                                    <textarea className="w-full p-2 border rounded text-sm" rows={2} value={slip.comments} onChange={e => updateSlip({
                              comments: e.target.value
                            })} data-id="element-4532" data-mp="1or74gbzfdew3" />
                                  </div>
                                  <Input label="P.C.A" value={slip.pca} onChange={e => updateSlip({
                            pca: e.target.value
                          })} data-id="element-4533" data-mp="12hqpv4px9toa" />
                                </div>
                              </div>
                              <div data-id="element-4534" data-mp="esfnjisxbr81">
                                <h5 className="font-semibold mb-2 text-pink-600" data-id="element-4535" data-mp="ere68wlbe8hv">
                                  Infant
                                </h5>
                                <div className="space-y-3" data-id="element-4536" data-mp="4u9mf3exyp48">
                                  <Input label="T.O.B (Time of Birth)" value={slip.infantTob} onChange={e => updateSlip({
                            infantTob: e.target.value
                          })} data-id="element-4537" data-mp="4w2rwfdtj3an" />
                                  <Input label="A/S (Apgar Score)" value={slip.apgarScore} onChange={e => updateSlip({
                            apgarScore: e.target.value
                          })} data-id="element-4538" data-mp="3jqtp3uycpbrr" />
                                  <Input label="Sex" value={slip.infantSex} onChange={e => updateSlip({
                            infantSex: e.target.value
                          })} data-id="element-4539" data-mp="78n56ycqweh3" />
                                </div>
                              </div>
                            </div>
                          </div>

                          <div data-id="element-4540" data-mp="2yecoib5oejlj">
                            <div className="flex justify-between items-end border-b pb-1 mb-2" data-id="element-4541" data-mp="2ydbbw290hp1p">
                              <h4 className="font-semibold text-sm uppercase" data-id="element-4542" data-mp="2th2sudrp1bp9">
                                Discharge Summary
                              </h4>
                              <div className="w-64" data-id="element-4543" data-mp="20sorpp5b5z9h">
                                <Input label="Date of Discharge" type="date" value={slip.dateOfDischarge} onChange={e => updateSlip({
                          dateOfDischarge: e.target.value
                        })} data-id="element-4544" data-mp="20psvc593zin5" />
                              </div>
                            </div>

                            <div className="bg-pink-50 p-3 rounded mb-4" data-id="element-4545" data-mp="11hp78ban5xd8">
                              <h5 className="font-semibold mb-3 uppercase text-sm" data-id="element-4546" data-mp="16fr0fp1s7d2f">
                                Mother
                              </h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-4547" data-mp="1af99nnk8pzpf">
                                <Input label="BP" value={slip.mother.bp} onChange={e => updateMother({
                          bp: e.target.value
                        })} data-id="element-4548" data-mp="1fgf69wh0u748" />
                                <Input label="Pallor" value={slip.mother.pallor} onChange={e => updateMother({
                          pallor: e.target.value
                        })} data-id="element-4549" data-mp="1y5bylusuypjt" />
                                <Input label="Breast" value={slip.mother.breast} onChange={e => updateMother({
                          breast: e.target.value
                        })} data-id="element-4550" data-mp="39osni3ohw1b4" />
                                <div data-id="element-4551" data-mp="3so6mj013028h">
                                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4552" data-mp="3sn51c2qcpzd1">
                                    Breastfeeding
                                  </label>
                                  <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" value={slip.mother.breastfeeding} onChange={e => updateMother({
                            breastfeeding: e.target.value
                          })} data-id="element-4553" data-mp="p53i6xffp27k">
                                    <option value="" data-id="element-4554" data-mp="2p0m6fwd6efk9">—</option>
                                    <option value="Yes" data-id="element-4555" data-mp="2twbaabw8aimd">Yes</option>
                                    <option value="No" data-id="element-4556" data-mp="2yw60sec3d66w">No</option>
                                  </select>
                                </div>
                                <Input label="Uterus" value={slip.mother.uterus} onChange={e => updateMother({
                          uterus: e.target.value
                        })} data-id="element-4557" data-mp="33vab58ar59yt" />
                                <Input label="Perineum" value={slip.mother.perineum} onChange={e => updateMother({
                          perineum: e.target.value
                        })} data-id="element-4558" data-mp="3hz7xmyfe9u9d" />
                                <Input label="Lochia" value={slip.mother.lochia} onChange={e => updateMother({
                          lochia: e.target.value
                        })} data-id="element-4559" data-mp="kko136xyaji" />
                                <Input label="Bowels" value={slip.mother.bowels} onChange={e => updateMother({
                          bowels: e.target.value
                        })} data-id="element-4560" data-mp="2hu74t913yf0h" />
                                <Input label="Urine" value={slip.mother.urine} onChange={e => updateMother({
                          urine: e.target.value
                        })} data-id="element-4561" data-mp="1z1397i4bxs38" />
                                <Input label="Family Planning" value={slip.mother.familyPlanning} onChange={e => updateMother({
                          familyPlanning: e.target.value
                        })} data-id="element-4562" data-mp="1g5v7r3q3dw32" />
                                <div className="md:col-span-2" data-id="element-4563" data-mp="16fwpvtnammk7">
                                  <Input label="Danger Signs" value={slip.mother.dangerSigns} onChange={e => updateMother({
                            dangerSigns: e.target.value
                          })} data-id="element-4564" data-mp="16iiubkew0ly7" />
                                </div>
                                <div className="md:col-span-2" data-id="element-4565" data-mp="2342onzxjakqk">
                                  <Input label="Treatment" value={slip.mother.treatment} onChange={e => updateMother({
                            treatment: e.target.value
                          })} data-id="element-4566" data-mp="1z652z72z2grx" />
                                </div>
                                <Input label="Vitamin A" value={slip.mother.vitaminA} onChange={e => updateMother({
                          vitaminA: e.target.value
                        })} data-id="element-4567" data-mp="39pq9pmfcbekr" />
                              </div>
                            </div>

                            <div className="bg-blue-50 p-3 rounded mb-4" data-id="element-4568" data-mp="12x7et7b3jxiv">
                              <h5 className="font-semibold mb-3 uppercase text-sm" data-id="element-4569" data-mp="2ngsz0rynkh7z">
                                Infant
                              </h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-4570" data-mp="2jfhev1yjso76">
                                <Input label="BWT (Birth Weight)" value={slip.infant.birthWeight} onChange={e => updateInfant({
                          birthWeight: e.target.value
                        })} data-id="element-4571" data-mp="2ememgx5ibksk" />
                                <Input label="Discharge Weight" value={slip.infant.dischargeWeight} onChange={e => updateInfant({
                          dischargeWeight: e.target.value
                        })} data-id="element-4572" data-mp="1qqa7rel12qab" />
                                <Input label="Pallor" value={slip.infant.pallor} onChange={e => updateInfant({
                          pallor: e.target.value
                        })} data-id="element-4573" data-mp="cvk0f9fccejm" />
                                <Input label="Jaundice" value={slip.infant.jaundice} onChange={e => updateInfant({
                          jaundice: e.target.value
                        })} data-id="element-4574" data-mp="vqqum8rc82x7" />
                                <div className="md:col-span-2" data-id="element-4575" data-mp="2zympkeo4omnp">
                                  <Input label="Passed Meconium / Urine" value={slip.infant.passedMeconiumUrine} onChange={e => updateInfant({
                            passedMeconiumUrine: e.target.value
                          })} data-id="element-4576" data-mp="300zexcw7tzas" />
                                </div>
                                <Input label="Feeding" value={slip.infant.feeding} onChange={e => updateInfant({
                          feeding: e.target.value
                        })} data-id="element-4577" data-mp="2q0ozrmqmehsz" />
                                <Input label="Feeding Option" value={slip.infant.feedingOption} onChange={e => updateInfant({
                          feedingOption: e.target.value
                        })} data-id="element-4578" data-mp="17zzqc2evgocq" />
                                <Input label="Breast Feeding" value={slip.infant.breastFeeding} onChange={e => updateInfant({
                          breastFeeding: e.target.value
                        })} data-id="element-4579" data-mp="1qy3k9pm6ugty" />
                                <Input label="BCG" value={slip.infant.bcg} onChange={e => updateInfant({
                          bcg: e.target.value
                        })} data-id="element-4580" data-mp="2enodd1c22ugy" />
                                <Input label="OPV" value={slip.infant.opv} onChange={e => updateInfant({
                          opv: e.target.value
                        })} data-id="element-4581" data-mp="3k9hp2dshfq1d" />
                                <Input label="Birth Record" value={slip.infant.birthRecord} onChange={e => updateInfant({
                          birthRecord: e.target.value
                        })} data-id="element-4582" data-mp="714wj6f43rhm" />
                                <Input label="G/C" value={slip.infant.gc} onChange={e => updateInfant({
                          gc: e.target.value
                        })} data-id="element-4583" data-mp="1wiqqdeb3mmg7" />
                                <div className="md:col-span-2" data-id="element-4584" data-mp="2fdnr2g2udljc">
                                  <Input label="Treatment" value={slip.infant.treatment} onChange={e => updateInfant({
                            treatment: e.target.value
                          })} data-id="element-4585" data-mp="2u3v2wcycg6b2" />
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-4586" data-mp="38rd77a7zuywy">
                              <Input label="6 days Postnatal Date" type="date" value={slip.sixDaysPostnatalDate} onChange={e => updateSlip({
                        sixDaysPostnatalDate: e.target.value
                      })} data-id="element-4587" data-mp="3csopeuojghv4" />
                              <Input label="6 Weeks Postnatal Date" type="date" value={slip.sixWeeksPostnatalDate} onChange={e => updateSlip({
                        sixWeeksPostnatalDate: e.target.value
                      })} data-id="element-4588" data-mp="2u04akh5ocdab" />
                              <div className="md:col-span-2" data-id="element-4589" data-mp="gvqvwjmgencg">
                                <Input label="Name in Full" value={slip.nameInFull} onChange={e => updateSlip({
                          nameInFull: e.target.value
                        })} data-id="element-4590" data-mp="cuoz09g0endn" />
                              </div>
                            </div>
                          </div>
                        </div>;
              })()}
                  </Card>

                  {(() => {
              const defaultVisit = {
                date: '',
                mother: {
                  generalWellBeing: '',
                  temp: '',
                  pulse: '',
                  bp: '',
                  urine: '',
                  head: '',
                  eyes: '',
                  ears: '',
                  mouth: '',
                  neck: '',
                  breasts: '',
                  ampills: '',
                  abdomenUterus: '',
                  groin: '',
                  perineum: '',
                  legs: '',
                  anus: '',
                  lochia: '',
                  cervixPerSpeculum: '',
                  familyPlanning: '',
                  treatmentOrAdvise: ''
                },
                infant: {
                  name: '',
                  weight: '',
                  date: '',
                  headCircumference: '',
                  length: '',
                  temp: '',
                  head: '',
                  eyes: '',
                  ears: '',
                  mouth: '',
                  neck: '',
                  chest: '',
                  abdomen: '',
                  cord: '',
                  genitaliaRectum: '',
                  extremities: '',
                  back: '',
                  neurologicExam: '',
                  feeding: '',
                  additionalNotes: ''
                }
              };
              const motherFieldOrder: Array<[keyof typeof defaultVisit.mother, string]> = [['generalWellBeing', 'General Well Being'], ['temp', 'Temp'], ['pulse', 'Pulse'], ['bp', 'BP'], ['urine', 'Urine'], ['head', 'Head'], ['eyes', 'Eyes'], ['ears', 'Ears'], ['mouth', 'Mouth'], ['neck', 'Neck'], ['breasts', 'Breasts'], ['ampills', 'Ampills'], ['abdomenUterus', 'Abdomen (Uterus)'], ['groin', 'Groin'], ['perineum', 'Perineum'], ['legs', 'Legs'], ['anus', 'Anus'], ['lochia', 'Lochia']];
              const infantFieldOrder: Array<[keyof typeof defaultVisit.infant, string]> = [['headCircumference', 'Head Circumference'], ['length', 'Length'], ['temp', 'Temp'], ['head', 'Head'], ['eyes', 'Eyes'], ['ears', 'Ears'], ['mouth', 'Mouth'], ['neck', 'Neck'], ['chest', 'Chest'], ['abdomen', 'Abdomen'], ['cord', 'Cord'], ['genitaliaRectum', 'Genitalia & Rectum'], ['extremities', 'Extremities'], ['back', 'Back'], ['neurologicExam', 'Neurologic Exam'], ['feeding', 'Feeding']];
              const renderVisitForm = (visitKey: 'postNatalOneWeek' | 'postNatalSixWeeks') => {
                const visit = (selectedMaternityFile as any)[visitKey] || defaultVisit;
                const updateVisit = (patch: Partial<typeof defaultVisit>) => {
                  setSelectedMaternityFile({
                    ...selectedMaternityFile,
                    [visitKey]: {
                      ...visit,
                      ...patch
                    }
                  });
                };
                const updateMother = (patch: Partial<typeof defaultVisit.mother>) => {
                  updateVisit({
                    mother: {
                      ...visit.mother,
                      ...patch
                    }
                  });
                };
                const updateInfant = (patch: Partial<typeof defaultVisit.infant>) => {
                  updateVisit({
                    infant: {
                      ...visit.infant,
                      ...patch
                    }
                  });
                };
                return <div className="space-y-6" data-id="element-4591" data-mp="1ehy9rs1d387t">
                          <Input label="Date" type="date" value={visit.date} onChange={e => updateVisit({
                    date: e.target.value
                  })} data-id="element-4592" data-mp="19map9pxtr38n" />

                          <div className="bg-pink-50 p-3 rounded" data-id="element-4593" data-mp="1sao028nxazaq">
                            <h5 className="font-semibold mb-3 uppercase text-sm" data-id="element-4594" data-mp="26b8g4x2geh0j">
                              Mother
                            </h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-4595" data-mp="22aewifw1nodg">
                              {motherFieldOrder.map(([key, label]) => <Input key={key} label={label} value={visit.mother[key]} onChange={e => updateMother({
                        [key]: e.target.value
                      } as any)} data-id="element-4596" data-mp="7wdytyc8d7ey" />)}
                            </div>

                            <div className="mt-4 space-y-3" data-id="element-4597" data-mp="3a7sr1053setr">
                              <h6 className="font-semibold text-sm uppercase" data-id="element-4598" data-mp="3f8ymh5q5eg6z">
                                Vaginal Examination
                              </h6>
                              <div data-id="element-4599" data-mp="31b0k6yp3qa7u">
                                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4600" data-mp="366z2c4ju6vzt">
                                  Cervix per speculum
                                </label>
                                <textarea className="w-full p-2 border rounded text-sm" rows={2} value={visit.mother.cervixPerSpeculum} onChange={e => updateMother({
                          cervixPerSpeculum: e.target.value
                        })} data-id="element-4601" data-mp="14gxbq98xxe94" />
                              </div>
                              <div data-id="element-4602" data-mp="2qoyshwzi9dy7">
                                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4603" data-mp="2vqo5vja7a3pg">
                                  Family Planning
                                </label>
                                <textarea className="w-full p-2 border rounded text-sm" rows={2} value={visit.mother.familyPlanning} onChange={e => updateMother({
                          familyPlanning: e.target.value
                        })} data-id="element-4604" data-mp="2lryp5tu1ipas" />
                              </div>
                              <div data-id="element-4605" data-mp="1cqybziba8y6o">
                                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4606" data-mp="1cp3zmse2uee7">
                                  Treatment or Advise
                                </label>
                                <textarea className="w-full p-2 border rounded text-sm" rows={3} value={visit.mother.treatmentOrAdvise} onChange={e => updateMother({
                          treatmentOrAdvise: e.target.value
                        })} data-id="element-4607" data-mp="k0gc0q5uvcxh" />
                              </div>
                            </div>
                          </div>

                          <div className="bg-blue-50 p-3 rounded" data-id="element-4608" data-mp="q2pgxpoaqvoa">
                            <h5 className="font-semibold mb-3 uppercase text-sm" data-id="element-4609" data-mp="q0uta3ln4pgi">
                              Infant Examination Form
                            </h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3" data-id="element-4610" data-mp="zrfpjkdudhw9">
                              <Input label="Infant Name" value={visit.infant.name} onChange={e => updateInfant({
                        name: e.target.value
                      })} data-id="element-4611" data-mp="33jfkycwtku0" />
                              <Input label="Weight" value={visit.infant.weight} onChange={e => updateInfant({
                        weight: e.target.value
                      })} data-id="element-4612" data-mp="1nhjr7m7gz7km" />
                              <Input label="Date" type="date" value={visit.infant.date} onChange={e => updateInfant({
                        date: e.target.value
                      })} data-id="element-4613" data-mp="338cj5igqubej" />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-4614" data-mp="2fci0o57wkkaf">
                              {infantFieldOrder.map(([key, label]) => <Input key={key} label={label} value={visit.infant[key]} onChange={e => updateInfant({
                        [key]: e.target.value
                      } as any)} data-id="element-4615" data-mp="ih2axj5gpcpk" />)}
                            </div>
                            <div className="mt-3" data-id="element-4616" data-mp="2u9ynjvkmic5">
                              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4617" data-mp="2sgpnxxhjm0z">
                                Additional Notes
                              </label>
                              <textarea className="w-full p-2 border rounded text-sm" rows={3} value={visit.infant.additionalNotes} onChange={e => updateInfant({
                        additionalNotes: e.target.value
                      })} data-id="element-4618" data-mp="3p3b8butmsxd0" />
                            </div>
                          </div>
                        </div>;
              };
              return <>
                        <Card title="Postnatal Visit — One Week" data-id="element-4619" data-mp="35f6l1t5thzkj">
                          {renderVisitForm('postNatalOneWeek')}
                        </Card>
                        <Card title="Postnatal Visit — Six Weeks" data-id="element-4620" data-mp="3fensaf0pwlim">
                          {renderVisitForm('postNatalSixWeeks')}
                        </Card>
                      </>;
            })()}
                </div>
              </MaternityErrorBoundary> : <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-500 h-full flex flex-col items-center justify-center" data-id="element-4621" data-mp="6xszxthjqjis">
                <Baby className="w-16 h-16 text-gray-300 mb-4" data-id="element-4622" data-mp="1xer4984f5jp" />
                <p className="text-lg font-medium text-gray-900" data-id="element-4623" data-mp="2vsshhvb21n6m">
                  Select a maternity file
                </p>
                <p data-id="element-4624" data-mp="2zqq3kp9j3t5s">
                  Choose a patient from the list to view and update their
                  maternity records.
                </p>
              </div>}
          </div>
        </div>}
    </div>;
};